"use client";

/**
 * GlobalVuStrip — professional audio visualiser popup.
 *
 * Six toggleable meters arranged in a 2-column grid:
 *   1. VU Meters     — LED stereo bar-graph with dB scale
 *   2. Needle VU     — Classic analog VU needle meter with cream face
 *   3. Spectrum      — FFT analyser with frequency & dBFS grid
 *   4. Oscilloscope  — time-domain waveform with amplitude grid
 *   5. Spectrogram   — scrolling waterfall with frequency axis
 *   6. Goniometer    — Lissajous stereo phase + correlation bar
 */

import React, { useEffect, useRef, useCallback, useState } from "react";
import { usePathname } from "next/navigation";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { useBranding } from "@/contexts/branding-context";
import { useAuth } from "@/contexts/auth-context";
import { useAdmin } from "@/hooks/use-admin";
import { saveAppearanceSettings } from "@/services/appearance-settings";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { X, Maximize2, Minimize2, GripVertical, Plus, Trash2, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCloseMenuWhenOpen } from "@/hooks/use-close-menu-when-open";

// ─── constants ────────────────────────────────────────────────────────────────

const SEGMENTS     = 32;         // LED segments per channel in VU
const PEAK_HOLD_MS = 1400;
const PEAK_FALL    = 0.5;

// Ballistics for the thin strip preview (higher = faster response per frame at 60 fps)
// Attack is INSTANT (1.0 = the meter jumps straight to a new peak the same
// frame it's measured) — any smoothing on the rise reads as the meter
// trailing the audio, which was still perceptible on mobile at 0.95 where
// RAF frames can be sparser. Real LED peak meters rise instantly; the
// characteristic "meter feel" lives entirely in the RELEASE, unchanged.
const VU_ATTACK  = 1.0;    // rise speed — instant
const VU_RELEASE = 0.28;   // fall speed — controlled decay

// Strip gain: small nudge so -18 dBFS RMS music sits at ~60% on the strip.
// With pure RMS and NO gain, -14 LUFS sits at (26/43) ≈ 60% — that's right.
// Don't add extra gain or peaks push everything into the red zone.
const VU_GAIN_DB = 0;

// Display range: -40 dBFS (bottom) to 0 dBFS (top) = 40 dB span.
// 0 dBFS is full scale — the meter now fills ALL the way to the top at 0 dB
// instead of stopping ~3 LEDs short (Gordon: "should go all the way to zero
// dB ... a few lights missing for it to go all the way up"). A clipped /
// 0 dBFS signal lights every segment (top red). 0 VU ref at -14 dBFS lands
// at (26/40) = 65 % height.
const VU_MIN_DB  = -40;
const VU_SCALE   = 40;

// ─── Meter modes ──────────────────────────────────────────────────────────────
type MeterMode = "rms" | "peak" | "vu" | "ppm" | "lufs-m" | "lufs-s" | "true-peak";

const METER_MODE_LABELS: Record<MeterMode, string> = {
  "vu":        "Analog",
  "rms":       "RMS",
  "peak":      "Peak",
  "ppm":       "PPM",
  "lufs-m":    "LUFS-M",
  "lufs-s":    "LUFS-S",
  "true-peak": "TrPk",
};

const METER_MODE_DESC: Record<MeterMode, string> = {
  "vu":        "Analog VU — 300ms integration, 0VU = ref level",
  "rms":       "Root Mean Square — power avg ~46ms",
  "peak":      "True peak — max sample per frame",
  "ppm":       "PPM EBU I — fast attack, 1.5s decay",
  "lufs-m":    "Momentary LUFS ~400ms (approx.)",
  "lufs-s":    "Short-term LUFS ~3s sliding (approx.)",
  "true-peak": "Approx. true peak w/ cubic interpolation",
};

interface ChannelMeterState {
  rmsSmoothed: number;
  vuSmoothLin: number;
  ppmDb:       number;
  lufsM:       number[];
  lufsS:       number[];
}

function makeMeterState(): ChannelMeterState {
  return { rmsSmoothed: -120, vuSmoothLin: 0, ppmDb: -120, lufsM: [], lufsS: [] };
}

// ─── Real sound only ─────────────────────────────────────────────────────────
// The meters never fake movement (Gordon 2026-10-31: "VU meter can never fake
// movement, always react on sound"). The random YouTube walk and the clock
// "pulse" are gone. Sound the page can't tap — a YouTube iframe, iOS audio
// without an analyser — shows the ROOM through coπ's microphone while it is
// listening (components/copi/copi-mic.tsx publishes the level); otherwise
// those meters stay still.
function micOn(): boolean {
  return typeof window !== "undefined" && (window as any).__bbMicOn === true;
}
function micDb(): number {
  const v = typeof window !== "undefined" ? (window as any).__bbMicDb : undefined;
  return typeof v === "number" && isFinite(v) ? v : VU_MIN_DB;
}

// ── Scroll-aware draw throttle ───────────────────────────────────────────────
// Repainting the always-visible strip canvases at 60fps WHILE the page is
// scrolling stacks canvas rasterisation on top of scroll compositing — on
// iPhone (especially in the standalone PWA, with player bar + FAB + header
// all composited) that combined load contributed to WebKit killing the
// renderer mid-scroll. Skip strip draws while a scroll is in flight; the
// meters resume within ~120ms of the last scroll event, which is invisible.
let _lastScrollTs = 0;
if (typeof window !== 'undefined') {
  const bump = () => { _lastScrollTs = Date.now(); };
  window.addEventListener('scroll', bump, { passive: true, capture: true });
  // Touch drags don't always emit 'scroll' (e.g. clamped overscroll after the
  // rubber-band fix) — treat any touch movement as scroll-in-flight too.
  window.addEventListener('touchmove', bump, { passive: true, capture: true });
}
function scrollingNow(): boolean {
  return Date.now() - _lastScrollTs < 120;
}

// Ground-truth playback check straight off the <audio> element(s). On iPhone the
// React `isPlaying` state can lag or desync (audio is routed outside Web Audio
// for lock-screen playback), which left the meters frozen even while a track was
// clearly playing. Reading the element's own paused/currentTime state fixes it.
// Cheap enough to call per frame at 20fps.
function audioLive(a: HTMLAudioElement | null | undefined): boolean {
  return !!a && !a.paused && !a.ended && a.currentTime > 0 && a.readyState >= 2;
}
function domAudioPlaying(): boolean {
  if (typeof window === 'undefined') return false;
  // The media player's element is created via `new Audio()` and is NOT in the
  // DOM, so a getElementsByTagName scan never finds it. Read the element the
  // player publishes on window first — this is the signal that keeps the iOS
  // meter alive (no analyser there, so it can't fall back to measured levels).
  const published = (window as unknown as { __bbAudioEl?: HTMLAudioElement }).__bbAudioEl;
  if (audioLive(published)) return true;
  if (typeof document === 'undefined') return false;
  const els = document.getElementsByTagName('audio');
  for (let i = 0; i < els.length; i++) {
    if (audioLive(els[i])) return true;
  }
  return false;
}

// Touch devices get 20fps meter redraws instead of 60 — visually identical
// for LED meters, but it cuts the always-on canvas rasterisation load to a
// third. That load, stacked on scroll compositing and page rendering, is a
// prime contributor to WebKit killing the renderer on iPhone.
// (Each RAF loop keeps its OWN last-draw timestamp — a shared one would let
// whichever canvas ticks first starve all the others every frame.)
const _coarsePointer = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
const _drawIntervalMs = _coarsePointer ? 50 : 0;

/** Ensure a canvas's backing store matches its container × devicePixelRatio.
 *  The ResizeObserver in the sizing effects can miss (canvas hidden at mount,
 *  display:none → block transitions, throttled background tabs), leaving the
 *  browser's DEFAULT 300×150 backing — a root cause of the dead/garbled
 *  always-on strips. Cheap per-frame check; returns false while the
 *  container has no layout size yet (strip hidden). */
function ensureCanvasSize(canvas: HTMLCanvasElement, container: HTMLElement | null, dpr: number): boolean {
  if (!container) return canvas.width > 0 && canvas.height > 0;
  const w = container.offsetWidth;
  const h = container.offsetHeight;
  // Container has no layout size RIGHT NOW (mid-animation, or the iOS flex
  // quirk inside the player sheet): keep drawing on whatever backing the
  // canvas already has instead of freezing — returning false here bricked
  // the sheet meters on iPhone ("not moving all of a sudden").
  if (w <= 0 || h <= 0) return canvas.width > 0 && canvas.height > 0;
  const tw = Math.round(w * dpr);
  const th = Math.round(h * dpr);
  if (canvas.width !== tw || canvas.height !== th) {
    canvas.width = tw;
    canvas.height = th;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  return true;
}

// ── Dead-signal fallback ─────────────────────────────────────────────────────
// If the analysers deliver pure digital silence while playback is active for
// a sustained stretch, the Web Audio graph is broken on this device (typical
// on iOS: context wedged after tab restore, element source silently
// disconnected, missing analyser) — the meters would sit dead with only the
// baseline LED lit even though music is audibly playing. Fall back to the
// simulated feed (same one YouTube playback uses) so the meters always move
// with playback. Any real signal from the analysers instantly resets the
// counter and takes priority. NOTE: real quiet passages still register well
// above VU_MIN (true zero only occurs when NO data flows), so genuine music
// can't trigger this.
// Per-caller tick state — this WAS one shared module counter, which every
// meter loop on the page incremented/reset concurrently (cross-talk made the
// sim trigger erratically). Each component passes its own box now.
export type SilentTicks = { t: number };
function resilientDb(realDb: number, playing: boolean, box: SilentTicks): number {
  if (!playing) { box.t = 0; return realDb; }
  if (realDb > VU_MIN_DB + 0.01) { box.t = 0; return realDb; }
  box.t++;
  // ~2 ticks/frame per meter pair → 600 ticks ≈ 5s of TRUE silence. The old
  // 0.5s threshold made the sim dance during normal startup BUFFERING, so
  // the VU visibly ran ahead of the actual audio (Gordon: "the VU starts
  // before the audio starts playing"). 5s only ever trips on genuinely
  // broken audio graphs (the iOS case this fallback exists for).
  return realDb; // a silent graph stays silent — the meter never fakes movement
}

function computeMeterDb(
  analyser: AnalyserNode,
  mode: MeterMode,
  state: ChannelMeterState
): number {
  const buf = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(buf);
  const n = buf.length;

  if (mode === "peak") {
    let pk = 0;
    for (let i = 0; i < n; i++) { const a = Math.abs(buf[i]); if (a > pk) pk = a; }
    return 20 * Math.log10(Math.max(pk, 1e-10));
  }

  if (mode === "true-peak") {
    let tpPeak = 0;
    for (let i = 1; i < n - 2; i++) {
      const s0 = buf[i-1], s1 = buf[i], s2 = buf[i+1], s3 = buf[i+2];
      const a0 = -0.5*s0 + 1.5*s1 - 1.5*s2 + 0.5*s3;
      const a1 = s0 - 2.5*s1 + 2*s2 - 0.5*s3;
      const a2 = -0.5*s0 + 0.5*s2;
      const interp = Math.abs(a0*0.125 + a1*0.25 + a2*0.5 + s1);
      if (interp > tpPeak) tpPeak = interp;
      if (Math.abs(s1) > tpPeak) tpPeak = Math.abs(s1);
    }
    return 20 * Math.log10(Math.max(tpPeak, 1e-10));
  }

  let sum = 0;
  for (let i = 0; i < n; i++) sum += buf[i] * buf[i];
  const rmsLin = Math.sqrt(sum / n);
  const dbRms  = 20 * Math.log10(Math.max(rmsLin, 1e-10));

  if (mode === "rms") {
    const a = dbRms > state.rmsSmoothed ? 0.25 : 0.06;
    state.rmsSmoothed += (dbRms - state.rmsSmoothed) * a;
    return state.rmsSmoothed;
  }
  if (mode === "vu") {
    // ~120 ms integration time (classic VU is 300 ms but that looks frozen on compressed music)
    // attack slightly faster than decay so the needle snaps up and floats down — feels analog
    const vuA = rmsLin > state.vuSmoothLin ? 0.18 : 0.10;
    state.vuSmoothLin += (rmsLin - state.vuSmoothLin) * vuA;
    return 20 * Math.log10(Math.max(state.vuSmoothLin, 1e-10));
  }
  if (mode === "ppm") {
    state.ppmDb += (dbRms - state.ppmDb) * (dbRms > state.ppmDb ? 0.86 : 0.011);
    return state.ppmDb;
  }
  if (mode === "lufs-m") {
    state.lufsM.push(rmsLin * rmsLin);
    if (state.lufsM.length > 24) state.lufsM.shift();
    const avg = state.lufsM.reduce((a, b) => a + b, 0) / state.lufsM.length;
    return 10 * Math.log10(Math.max(avg, 1e-20)) - 0.691;
  }
  if (mode === "lufs-s") {
    state.lufsS.push(rmsLin * rmsLin);
    if (state.lufsS.length > 180) state.lufsS.shift();
    const avg = state.lufsS.reduce((a, b) => a + b, 0) / state.lufsS.length;
    return 10 * Math.log10(Math.max(avg, 1e-20)) - 0.691;
  }
  return dbRms;
}

type VisType = "vu" | "needle-vu" | "spectrum" | "scope" | "spectrogram" | "goniometer";

const VIS_LABELS: Record<VisType, string> = {
  vu:          "VU",
  "needle-vu": "Needle",
  spectrum:    "Spectrum",
  scope:       "Scope",
  spectrogram: "Gram",
  goniometer:  "Gonio",
};

const VIS_FULL: Record<VisType, string> = {
  vu:          "VU Meters",
  "needle-vu": "Analog VU",
  spectrum:    "Spectrum Analyser",
  scope:       "Oscilloscope",
  spectrogram: "Spectrogram",
  goniometer:  "Goniometer",
};

const VIS_COLORS: Record<VisType, string> = {
  vu:          "#22d35e",
  "needle-vu": "#e8b040",
  spectrum:    "#4d8fff",
  scope:       "#22d4d4",
  spectrogram: "#c944ff",
  goniometer:  "#00e5ff",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Shared scratch buffer for time-domain reads. getRms/getPeak run per channel
// per meter per animation frame (60fps) — allocating a fresh 2048-float array
// each call produced constant GC churn, whose collection pauses showed up as
// visible meter micro-hitches on mobile. Sequential calls within a frame can
// safely share one buffer (each call consumes its data before the next).
let _tdScratch = new Float32Array(2048);
function timeDomain(analyser: AnalyserNode): Float32Array {
  if (_tdScratch.length < analyser.fftSize) _tdScratch = new Float32Array(analyser.fftSize);
  const buf = _tdScratch.subarray(0, analyser.fftSize);
  analyser.getFloatTimeDomainData(buf);
  return buf;
}

function getRms(analyser: AnalyserNode): number {
  const buf = timeDomain(analyser);
  const bufLen = buf.length;
  const window = Math.min(512, bufLen);
  const start = bufLen - window;
  let s = 0;
  for (let i = start; i < bufLen; i++) s += buf[i] * buf[i];
  const rms = Math.sqrt(s / window);
  return 20 * Math.log10(Math.max(rms, 1e-10)) + VU_GAIN_DB;
}

/** True peak — max absolute sample in the current frame. Fast-attack, used by the strip. */
function getPeak(analyser: AnalyserNode): number {
  const buf = timeDomain(analyser);
  let pk = 0;
  for (let i = 0; i < buf.length; i++) {
    const a = Math.abs(buf[i]);
    if (a > pk) pk = a;
  }
  return 20 * Math.log10(Math.max(pk, 1e-10)) + VU_GAIN_DB;
}

/** LED colour for a given dB value (absolute dBFS zones — used by the thin strip) */
function ledColor(db: number, lit: boolean): string {
  if (!lit) return "rgba(30,32,30,0.9)";
  if (db >= -1)  return "#ff1414";   // clipping — red
  if (db >= -3)  return "#ff4400";   // hot — orange-red
  if (db >= -6)  return "#ff8800";   // warning — orange
  if (db >= -10) return "#ffe000";   // caution — yellow
  if (db >= -18) return "#88e030";   // safe-high — yellow-green
  return "#20e060";                   // nominal — green
}

/**
 * LED colour relative to a VU reference level.
 * Zones are expressed in VU units (vu = db - refDbfs):
 *   > +3 VU  → red    (clipping danger)
 *   0..+3 VU → orange (above nominal)
 *   -3..0 VU → yellow (close to nominal)
 *   -10..-3  → yellow-green
 *   < -10 VU → green
 * This makes the colour scale shift when the user picks -14, -18, -20 or -23 dBFS.
 */
function ledColorRef(db: number, lit: boolean, refDbfs: number): string {
  if (!lit) return "rgba(30,32,30,0.9)";
  const vu = db - refDbfs;          // VU units: 0 = reference level
  if (vu >= 3)   return "#ff1414";  // +3 VU and above — red
  if (vu >= 0)   return "#ff8800";  // 0..+3 VU — orange
  if (vu >= -3)  return "#ffe000";  // -3..0 VU — yellow (near nominal)
  if (vu >= -10) return "#88e030";  // -10..-3 VU — yellow-green
  return "#20e060";                  // below -10 VU — green
}

// ─── VU Strip colour schemes ──────────────────────────────────────────────────

type VuColorScheme = "classic" | "neon" | "mono-white" | "mono-amber" | "ice" | "retro";

const VU_COLOR_SCHEMES: Record<VuColorScheme, { unlit: string; nominal: string; safeHigh: string; caution: string; warning: string; hot: string; clip: string }> = {
  "classic":    { unlit: "rgba(30,32,30,0.9)",  nominal: "#20e060",                  safeHigh: "#88e030",                  caution: "#ffe000", warning: "#ff8800", hot: "#ff4400", clip: "#ff1414" },
  "neon":       { unlit: "rgba(10,10,20,0.9)",   nominal: "#00ffcc",                  safeHigh: "#00e5ff",                  caution: "#c8ff00", warning: "#ff6600", hot: "#ff2288", clip: "#ff0044" },
  "mono-white": { unlit: "rgba(20,20,20,0.9)",   nominal: "rgba(255,255,255,0.5)",    safeHigh: "rgba(255,255,255,0.65)",   caution: "rgba(255,255,255,0.78)", warning: "rgba(255,255,255,0.9)", hot: "#ffffff", clip: "#ffffff" },
  "mono-amber": { unlit: "rgba(20,15,5,0.9)",    nominal: "#8b5e00",                  safeHigh: "#bf8400",                  caution: "#e8a800", warning: "#ffbe00", hot: "#ffd040", clip: "#ffe880" },
  "ice":        { unlit: "rgba(5,10,20,0.9)",    nominal: "#1e88e5",                  safeHigh: "#29b6f6",                  caution: "#80d8ff", warning: "#b0e8ff", hot: "#e0f4ff", clip: "#ffffff" },
  "retro":      { unlit: "rgba(15,10,5,0.9)",    nominal: "#2e7d32",                  safeHigh: "#558b2f",                  caution: "#f9a825", warning: "#e65100", hot: "#b71c1c", clip: "#ff1744" },
};

// ─── Meter layout presets ─────────────────────────────────────────────────────

/** Default flex-grow value for each meter type */
// Widths from Gordon's screenshots (2026-11-01): VU slim, Spectrogram half,
// Analog VU the widest ("a little wider"), Oscilloscope and Goniometer after it.
const DEFAULT_METER_FLEXES: Record<VisType, number> = {
  "vu":          0.30,
  "needle-vu":   1.20,
  "spectrum":    1.00,
  "scope":       0.80,
  "spectrogram": 0.50,
  "goniometer":  0.90,
};

// Default arrangement — top row: VU, Spectrogram, Analog VU, Oscilloscope,
// Goniometer; Spectrum Analyser sits full-width on the bottom row (see
// bottomRowVis default). This mirrors Gordon's preferred meter layout.
const DEFAULT_METER_ORDER: VisType[] = ["vu", "spectrogram", "needle-vu", "scope", "goniometer", "spectrum"];

interface MeterLayoutPreset {
  name: string;
  order: VisType[];
  flexes: Record<VisType, number>;
  activeMeters: VisType[];
}

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch { return fallback; }
}
function lsSet(key: string, val: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

/** Device class for the admin-saved app-default layouts. */
type VuDeviceClass = "desktop" | "tablet" | "mobile";
function vuDeviceClass(): VuDeviceClass {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1100) return "tablet";
  return "desktop";
}

/**
 * iOS 15 / Safari <16 polyfill — ctx.roundRect() was added in Safari 16 (Sep 2022).
 * Calling it without a fallback throws TypeError on every RAF frame and crashes the tab.
 * Must be called AFTER ctx.beginPath() — the path is already open.
 */
function ctxRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (ctx as any).roundRect === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ctx as any).roundRect(x, y, w, h, r);
    return;
  }
  // Manual fallback for iOS 15 and below
  if (r > w / 2) r = w / 2;
  if (r > h / 2) r = h / 2;
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y,     x + w, y + r,     r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x,     y + h, x,     y + h - r, r);
  ctx.lineTo(x,     y + r);
  ctx.arcTo(x,     y,     x + r, y,         r);
  ctx.closePath();
}

interface VuStripPrefs {
  colorScheme: VuColorScheme;
  glowLevel: "none" | "soft" | "medium" | "heavy" | "star";
  opacity: number; // 0.2–1.0
}

const DEFAULT_VU_STRIP_PREFS: VuStripPrefs = {
  colorScheme: "classic",
  glowLevel: "medium",
  opacity: 1.0,
};

/** LED colour for a given dB value using a named colour scheme */
// Theme-aware unlit segment colour: every scheme's hardcoded near-black
// `unlit` made the always-visible strip render as a dark column on light
// themes. Read the active theme's --muted token instead (cached ~1s —
// getComputedStyle every frame would force style recalc at 60fps) and fall
// back to the scheme's own unlit if the token is unavailable.
let _unlitCache = { t: 0, v: "" };
function themeUnlit(fallback: string): string {
  const now = Date.now();
  if (now - _unlitCache.t > 1000) {
    _unlitCache.t = now;
    try {
      const raw = getComputedStyle(document.documentElement).getPropertyValue("--muted").trim();
      _unlitCache.v = raw ? `hsl(${raw})` : "";
    } catch { _unlitCache.v = ""; }
  }
  return _unlitCache.v || fallback;
}

function ledColorScheme(db: number, lit: boolean, scheme: VuColorScheme): string {
  const c = VU_COLOR_SCHEMES[scheme];
  if (!lit) return themeUnlit(c.unlit);
  if (db >= -1)  return c.clip;
  if (db >= -3)  return c.hot;
  if (db >= -6)  return c.warning;
  if (db >= -10) return c.caution;
  if (db >= -18) return c.safeHigh;
  return c.nominal;
}

// ─── strip canvas (narrow vertical, always visible) ───────────────────────────

function useVuStripCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  containerRef: React.RefObject<HTMLElement | null>,
  prefs: VuStripPrefs = DEFAULT_VU_STRIP_PREFS
) {
  const { analyserL, analyserR, isVisualizerActive, isExternalYT, currentTrack, isPlaying } = useMediaPlayer();
  const raf       = useRef(0);
  const peakL     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const peakR     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const smoothedL = useRef(VU_MIN_DB);
  const smoothedR = useRef(VU_MIN_DB);

  // Ref mirrors for volatile media-player values — avoids restarting RAF on every play/pause
  const analyserLRef          = useRef(analyserL);
  const analyserRRef          = useRef(analyserR);
  const isVisualizerActiveRef = useRef(isVisualizerActive);
  const isExternalYTRef       = useRef(isExternalYT);
  const currentTrackRef       = useRef(currentTrack);
  const isPlayingRef          = useRef(isPlaying);
  const silentBox = useRef<SilentTicks>({ t: 0 });
  const prefsRef              = useRef(prefs);
  useEffect(() => { analyserLRef.current          = analyserL;          }, [analyserL]);
  useEffect(() => { analyserRRef.current          = analyserR;          }, [analyserR]);
  useEffect(() => { isVisualizerActiveRef.current = isVisualizerActive; }, [isVisualizerActive]);
  useEffect(() => { isExternalYTRef.current       = isExternalYT;       }, [isExternalYT]);
  useEffect(() => { currentTrackRef.current       = currentTrack;       }, [currentTrack]);
  useEffect(() => { isPlayingRef.current          = isPlaying;          }, [isPlaying]);
  useEffect(() => { prefsRef.current              = prefs;              }, [prefs]);

  const draw = useCallback(() => {
    // Shadow names with ref reads so the RAF loop never needs to restart
    const analyserL          = analyserLRef.current;
    const analyserR          = analyserRRef.current;
    const isVisualizerActive = isVisualizerActiveRef.current;
    const isExternalYT       = isExternalYTRef.current;
    const currentTrack       = currentTrackRef.current;
    const isPlaying          = isPlayingRef.current;
    const prefs              = prefsRef.current;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    // Apply opacity pref
    ctx.globalAlpha = prefs.opacity;

    const now = Date.now();
    const isYT = !!(currentTrack?.youtubeId || currentTrack?.mediaType === "youtube") || isExternalYT;
    // External sources (hero video sound, YT iframes) count as playing — they
    // have no analyser feed, so the sim is what keeps the VU alive for them.
    const effPlaying = isPlaying || isExternalYT || domAudioPlaying();
    const hasRealL = !!(analyserL && isVisualizerActive && !isYT);
    const hasRealR = !!(analyserR && isVisualizerActive && !isYT);
    // Real analyser (desktop) → measured level with dead-signal recovery.
    // External YT → the lively random sim. iOS/no-analyser regular audio →
    // a smooth DETERMINISTIC pulse while playing (no more random dance), else silent.
    const dbL = micOn() ? micDb() : hasRealL ? resilientDb(getPeak(analyserL), effPlaying, silentBox.current)
              : VU_MIN_DB;
    // Publish a normalized 0..1 level for background sound-reaction (theme
    // engine reads this at ~15fps — a bare global write is effectively free).
    (window as any).__bbAudioLevel = Math.max(0, Math.min(1, (dbL + 48) / 48));
    const dbR = micOn() ? micDb() : hasRealR ? resilientDb(getPeak(analyserR), effPlaying, silentBox.current)
              : VU_MIN_DB;

    // IIR ballistic smoothing — fast attack, moderate release for peak mode
    smoothedL.current += (dbL - smoothedL.current) * (dbL > smoothedL.current ? VU_ATTACK : VU_RELEASE);
    smoothedR.current += (dbR - smoothedR.current) * (dbR > smoothedR.current ? VU_ATTACK : VU_RELEASE);
    const smL = smoothedL.current;
    const smR = smoothedR.current;

    for (const [db, peak] of [[dbL, peakL.current], [dbR, peakR.current]] as [number, typeof peakL.current][]) {
      if (db > peak.db) { peak.db = db; peak.heldAt = now; }
      else if (now - peak.heldAt > PEAK_HOLD_MS) { peak.db -= PEAK_FALL; if (peak.db < VU_MIN_DB) peak.db = VU_MIN_DB; }
    }

    const GAP = 2;
    const chW  = (W - GAP) / 2;
    const segH = H / SEGMENTS;

    // Glow helpers
    const applyGlow = (isLit: boolean, isPeak: boolean, color: string) => {
      ctx.shadowColor = color;
      if (!isLit) { ctx.shadowBlur = 0; return; }
      switch (prefs.glowLevel) {
        case "none":   ctx.shadowBlur = 0; break;
        case "soft":   ctx.shadowBlur = 3; break;
        case "medium": ctx.shadowBlur = isPeak ? 12 : 6; break;
        case "heavy":  ctx.shadowBlur = isPeak ? 20 : 12; break;
        case "star":   ctx.shadowBlur = isPeak ? 20 : 12; break;
      }
    };

    const drawCh = (cx: number, dbVal: number, peak: typeof peakL.current) => {
      for (let s = 0; s < SEGMENTS; s++) {
        const db  = VU_MIN_DB + (s / (SEGMENTS - 1)) * VU_SCALE;
        const lit = dbVal >= db;
        const y   = H - (s + 1) * segH + 0.5;
        const color = ledColorScheme(db, lit, prefs.colorScheme);
        applyGlow(lit, false, color);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctxRoundRect(ctx, cx, y, chW, segH - 1, 1);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      if (peak.db > VU_MIN_DB) {
        const ps = Math.round(((peak.db - VU_MIN_DB) / VU_SCALE) * (SEGMENTS - 1));
        const py = H - (Math.min(ps, SEGMENTS - 1) + 1) * segH;
        const peakDb = VU_MIN_DB + (ps / (SEGMENTS - 1)) * VU_SCALE;
        const peakColor = ledColorScheme(peakDb, true, prefs.colorScheme);
        applyGlow(true, true, peakColor);
        ctx.fillStyle = peakColor;
        ctx.globalAlpha = prefs.opacity * 0.9;
        ctx.fillRect(cx, py + 0.5, chW, 2);
        ctx.globalAlpha = prefs.opacity;
        // Star sparkle at peak when glowLevel === "star"
        if (prefs.glowLevel === "star") {
          const pcx = cx + chW / 2;
          const pcy = py + 1;
          ctx.strokeStyle = peakColor;
          ctx.lineWidth = 0.5;
          ctx.shadowBlur = 8;
          for (let a = 0; a < 4; a++) {
            const angle = (a * Math.PI) / 2;
            ctx.beginPath();
            ctx.moveTo(pcx, pcy);
            ctx.lineTo(pcx + Math.cos(angle) * 4, pcy + Math.sin(angle) * 4);
            ctx.stroke();
          }
        }
        ctx.shadowBlur = 0;
      }
    };

    drawCh(0, smL, peakL.current);
    drawCh(chW + GAP, smR, peakR.current);

    ctx.restore();
  }, [canvasRef]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = container.offsetWidth  * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width  = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [canvasRef, containerRef]);
}

// ─── horizontal strip canvas (mobile — two LED rows, L top / R bottom) ────────

function useVuHorizontalStripCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  containerRef: React.RefObject<HTMLElement | null>
) {
  const { analyserL, analyserR, isVisualizerActive, isExternalYT, currentTrack, isPlaying } = useMediaPlayer();
  const raf       = useRef(0);
  const peakL     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const peakR     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const smoothedL = useRef(VU_MIN_DB);
  const smoothedR = useRef(VU_MIN_DB);

  // Ref mirrors for volatile media-player values — avoids restarting RAF on every play/pause
  const analyserLRef          = useRef(analyserL);
  const analyserRRef          = useRef(analyserR);
  const isVisualizerActiveRef = useRef(isVisualizerActive);
  const isExternalYTRef       = useRef(isExternalYT);
  const currentTrackRef       = useRef(currentTrack);
  const isPlayingRef          = useRef(isPlaying);
  const silentBox = useRef<SilentTicks>({ t: 0 });
  useEffect(() => { analyserLRef.current          = analyserL;          }, [analyserL]);
  useEffect(() => { analyserRRef.current          = analyserR;          }, [analyserR]);
  useEffect(() => { isVisualizerActiveRef.current = isVisualizerActive; }, [isVisualizerActive]);
  useEffect(() => { isExternalYTRef.current       = isExternalYT;       }, [isExternalYT]);
  useEffect(() => { currentTrackRef.current       = currentTrack;       }, [currentTrack]);
  useEffect(() => { isPlayingRef.current          = isPlaying;          }, [isPlaying]);

  const draw = useCallback(() => {
    // Shadow names with ref reads so the RAF loop never needs to restart
    const analyserL          = analyserLRef.current;
    const analyserR          = analyserRRef.current;
    const isVisualizerActive = isVisualizerActiveRef.current;
    const isExternalYT       = isExternalYTRef.current;
    const currentTrack       = currentTrackRef.current;
    const isPlaying          = isPlayingRef.current;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    const now  = Date.now();
    const isYT2 = !!(currentTrack?.youtubeId || currentTrack?.mediaType === "youtube") || isExternalYT;
    const effPlaying2 = isPlaying || isExternalYT || domAudioPlaying();
    const hasRealL2 = !!(analyserL && isVisualizerActive && !isYT2);
    const hasRealR2 = !!(analyserR && isVisualizerActive && !isYT2);
    const dbL  = micOn() ? micDb() : hasRealL2 ? resilientDb(getPeak(analyserL), effPlaying2, silentBox.current)
               : VU_MIN_DB;
    const dbR  = micOn() ? micDb() : hasRealR2 ? resilientDb(getPeak(analyserR), effPlaying2, silentBox.current)
               : VU_MIN_DB;

    // IIR ballistic smoothing — fast attack, moderate release for peak mode
    smoothedL.current += (dbL - smoothedL.current) * (dbL > smoothedL.current ? VU_ATTACK : VU_RELEASE);
    smoothedR.current += (dbR - smoothedR.current) * (dbR > smoothedR.current ? VU_ATTACK : VU_RELEASE);
    const smL = smoothedL.current;
    const smR = smoothedR.current;

    for (const [db, peak] of [[dbL, peakL.current], [dbR, peakR.current]] as [number, typeof peakL.current][]) {
      if (db > peak.db) { peak.db = db; peak.heldAt = now; }
      else if (now - peak.heldAt > PEAK_HOLD_MS) {
        peak.db -= PEAK_FALL;
        if (peak.db < VU_MIN_DB) peak.db = VU_MIN_DB;
      }
    }

    // Two horizontal LED rows: top = L, bottom = R, 1px gap between
    const GAP  = 1;
    const rowH = (H - GAP) / 2;
    const segW = W / SEGMENTS;
    const segGap = 0.8;

    const drawRow = (rowY: number, dbVal: number, peak: typeof peakL.current) => {
      for (let s = 0; s < SEGMENTS; s++) {
        const db  = VU_MIN_DB + (s / (SEGMENTS - 1)) * VU_SCALE;
        const lit = dbVal >= db;
        // Mobile strip: unlit segments stay transparent (clearRect background shows through)
        if (!lit) continue;
        const x   = s * segW;
        ctx.fillStyle = ledColor(db, lit);
        ctx.beginPath();
        ctxRoundRect(ctx, x + segGap / 2, rowY, Math.max(segW - segGap, 1), rowH, 1);
        ctx.fill();
      }
      // Peak-hold tick (vertical line at peak segment)
      if (peak.db > VU_MIN_DB) {
        const ps = Math.round(((peak.db - VU_MIN_DB) / VU_SCALE) * (SEGMENTS - 1));
        const px = Math.min(ps, SEGMENTS - 1) * segW + segW - 1.5;
        ctx.fillStyle = ledColor(VU_MIN_DB + (ps / (SEGMENTS - 1)) * VU_SCALE, true);
        ctx.globalAlpha = 0.9;
        ctx.fillRect(px, rowY, 1.5, rowH);
        ctx.globalAlpha = 1;
      }
    };

    drawRow(0,          smL, peakL.current);
    drawRow(rowH + GAP, smR, peakR.current);
    ctx.restore();
  }, [canvasRef]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);

  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = container.offsetWidth  * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width  = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [canvasRef, containerRef]);
}

// ─── reusable canvas resize hook ──────────────────────────────────────────────

function useResizeCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  onResize?: () => void
) {
  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = container.offsetWidth  * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width  = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
      onResize?.();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [canvasRef, containerRef, onResize]);
}

// ─── shared canvas draw helpers ───────────────────────────────────────────────

const BG      = "#080c0a";
const GRID    = "rgba(255,255,255,0.07)";
const GRIDHI  = "rgba(255,255,255,0.15)";
const LABEL   = "rgba(200,220,200,0.55)";
const LABELBR = "rgba(200,220,200,0.85)";

// ─── VU popup canvas ──────────────────────────────────────────────────────────

export function VuPopupCanvas({ meterMode, vuRefDbfs = -14 }: { meterMode: MeterMode; vuRefDbfs?: number }) {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { analyserL, analyserR, isVisualizerActive, isExternalYT, currentTrack, isPlaying } = useMediaPlayer();
  const raf       = useRef(0);
  const peakL     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const peakR     = useRef({ db: VU_MIN_DB, heldAt: 0 });
  const stateL = useRef<ChannelMeterState>(makeMeterState());
  const stateR = useRef<ChannelMeterState>(makeMeterState());
  // Per-component silence counter + live playback flag (stale isPlaying in
  // this closure made the sim dance the meters after pause — see resilientDb).
  const silentBox = useRef<SilentTicks>({ t: 0 });
  const isPlayingRef3 = useRef(isPlaying);
  useEffect(() => { isPlayingRef3.current = isPlaying; }, [isPlaying]);

  // dB scale marks to show on the VU meter (range: -40 to +3 dBFS)
  const SCALE_MARKS = [-40, -20, -10, -6, -3, -1, 0, 3];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    // Layout: 28px scale rail on right, 6px gap, rest = two channels
    const SCALE_W  = 28;
    const CH_GAP   = 4;
    const LBL_H    = 14;  // bottom label area for L/R text
    const BAR_H    = H - LBL_H - 2;
    const chW      = (W - SCALE_W - CH_GAP) / 2 - CH_GAP;
    const segH     = BAR_H / SEGMENTS;

    const now = Date.now();
    const isYT3 = !!(currentTrack?.youtubeId || currentTrack?.mediaType === "youtube") || isExternalYT;
    // Live flag via ref — the closure's isPlaying is stale (not in deps).
    // Include the ground-truth <audio> element check so iOS (no analyser) keeps
    // the meter alive even when the React isPlaying flag lags.
    const playingNow3 = isPlayingRef3.current || isExternalYT || domAudioPlaying();
    const hasRealL3 = !!(analyserL && isVisualizerActive && !isYT3);
    const hasRealR3 = !!(analyserR && isVisualizerActive && !isYT3);
    const dbL = micOn() ? micDb() : hasRealL3 ? resilientDb(computeMeterDb(analyserL, meterMode, stateL.current), playingNow3, silentBox.current)
              : VU_MIN_DB;
    const dbR = micOn() ? micDb() : hasRealR3 ? resilientDb(computeMeterDb(analyserR, meterMode, stateR.current), playingNow3, silentBox.current)
              : VU_MIN_DB;

    const smL = dbL;
    const smR = dbR;

    for (const [db, peak] of [[dbL, peakL.current], [dbR, peakR.current]] as [number, typeof peakL.current][]) {
      if (db > peak.db) { peak.db = db; peak.heldAt = now; }
      else if (now - peak.heldAt > PEAK_HOLD_MS) { peak.db -= PEAK_FALL; if (peak.db < VU_MIN_DB) peak.db = VU_MIN_DB; }
    }

    // Use ref-relative colouring so changing the 0VU knob visibly shifts zones
    const colorFn = (db: number, lit: boolean) => ledColorRef(db, lit, vuRefDbfs);

    const drawCh = (cx: number, dbVal: number, peak: typeof peakL.current) => {
      for (let s = 0; s < SEGMENTS; s++) {
        const db  = VU_MIN_DB + (s / (SEGMENTS - 1)) * VU_SCALE;
        const lit = dbVal >= db;
        const y   = BAR_H - (s + 1) * segH + 0.5;
        ctx.fillStyle = colorFn(db, lit);
        ctx.beginPath();
        ctxRoundRect(ctx, cx, y, chW, Math.max(segH - 1.5, 1), 1.5);
        ctx.fill();
      }
      // Peak hold dot
      if (peak.db > VU_MIN_DB) {
        const ps = Math.round(((peak.db - VU_MIN_DB) / VU_SCALE) * (SEGMENTS - 1));
        const py = BAR_H - (Math.min(ps, SEGMENTS - 1) + 1) * segH;
        ctx.fillStyle = colorFn(VU_MIN_DB + (ps / (SEGMENTS - 1)) * VU_SCALE, true);
        ctx.globalAlpha = 0.92;
        ctx.fillRect(cx, py + 0.5, chW, 3);
        ctx.globalAlpha = 1;
      }
    };

    drawCh(CH_GAP, smL, peakL.current);
    drawCh(CH_GAP + chW + CH_GAP, smR, peakR.current);

    // ── dB scale rail (right) ──────────────────────────────────────
    const scaleX = W - SCALE_W;
    ctx.fillStyle = "rgba(10,12,10,0.9)";
    ctx.fillRect(scaleX, 0, SCALE_W, BAR_H);

    ctx.font = `bold ${Math.round(8.5 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    if (meterMode === "vu") {
      // VU mode: draw scale in VU units, positioned at their dBFS equivalent
      const VU_MARKS = [-20, -10, -7, -3, 0, 3]; // VU values to label
      for (const vuMark of VU_MARKS) {
        const dbfs  = vuMark + vuRefDbfs;   // convert VU → dBFS position on bar
        const ratio = (dbfs - VU_MIN_DB) / VU_SCALE;
        if (ratio < 0 || ratio > 1) continue; // skip if off-scale
        const y = BAR_H - ratio * BAR_H;
        const tickColor = vuMark >= 3 ? "#ff4444" : vuMark >= 0 ? "#ffaa00" : LABEL;
        const lblColor  = vuMark >= 3 ? "#ff6666" : vuMark >= 0 ? "#ffcc55" : LABEL;
        ctx.fillStyle = tickColor;
        ctx.fillRect(scaleX + 1, y, 4, 1);
        ctx.fillStyle = lblColor;
        const lbl = vuMark > 0 ? `+${vuMark}` : `${vuMark}`;
        ctx.fillText(lbl, W - 2, y + 3.5);
      }
    } else {
      // Other modes: absolute dBFS scale
      for (const db of SCALE_MARKS) {
        const ratio = (db - VU_MIN_DB) / VU_SCALE;
        const y = BAR_H - ratio * BAR_H;
        // Colour ticks relative to ref level so they shift with the setting
        const vu = db - vuRefDbfs;
        const tickColor = vu >= 3 ? "#ff4444" : vu >= 0 ? "#ffaa00" : LABEL;
        const lblColor  = vu >= 3 ? "#ff6666" : vu >= 0 ? "#ffcc55" : LABEL;
        ctx.fillStyle = tickColor;
        ctx.fillRect(scaleX + 1, y, 4, 1);
        ctx.fillStyle = lblColor;
        ctx.fillText(db === 0 ? "0" : `${db}`, W - 2, y + 3.5);
      }
    }

    // Unit label — "VU" in VU mode, "dBFS" otherwise
    ctx.fillStyle = "rgba(180,200,180,0.35)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText(meterMode === "vu" ? "VU" : "dBFS", scaleX + SCALE_W / 2, BAR_H - 2);

    // Mode label above dBFS
    ctx.fillStyle = "rgba(180,200,180,0.5)";
    ctx.font = `bold ${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText(METER_MODE_LABELS[meterMode], scaleX + SCALE_W / 2, BAR_H - 10);

    // Zone boundary lines — shifted by ref level so they track the 0VU line
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    // Green → yellow-green at (ref - 10) dBFS
    const yGY = BAR_H - ((vuRefDbfs - 10 - VU_MIN_DB) / VU_SCALE) * BAR_H;
    if (yGY >= 0 && yGY <= BAR_H) {
      ctx.strokeStyle = "rgba(180,255,100,0.18)";
      ctx.beginPath(); ctx.moveTo(CH_GAP, yGY); ctx.lineTo(scaleX - 2, yGY); ctx.stroke();
    }
    // Yellow → orange at (ref - 3) dBFS
    const yYO = BAR_H - ((vuRefDbfs - 3 - VU_MIN_DB) / VU_SCALE) * BAR_H;
    if (yYO >= 0 && yYO <= BAR_H) {
      ctx.strokeStyle = "rgba(255,200,50,0.25)";
      ctx.beginPath(); ctx.moveTo(CH_GAP, yYO); ctx.lineTo(scaleX - 2, yYO); ctx.stroke();
    }
    // Orange → red at (ref + 3) dBFS
    const yOR = BAR_H - ((vuRefDbfs + 3 - VU_MIN_DB) / VU_SCALE) * BAR_H;
    if (yOR >= 0 && yOR <= BAR_H) {
      ctx.strokeStyle = "rgba(255,80,20,0.30)";
      ctx.beginPath(); ctx.moveTo(CH_GAP, yOR); ctx.lineTo(scaleX - 2, yOR); ctx.stroke();
    }
    ctx.setLineDash([]);

    // 0 VU reference line — shows target level for current ref setting
    const refRatio = (vuRefDbfs - VU_MIN_DB) / VU_SCALE;
    if (refRatio >= 0 && refRatio <= 1) {
      const yRef = BAR_H - refRatio * BAR_H;
      ctx.save();
      ctx.strokeStyle = "rgba(255,220,80,0.75)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      ctx.beginPath(); ctx.moveTo(CH_GAP, yRef); ctx.lineTo(scaleX - 2, yRef); ctx.stroke();
      ctx.setLineDash([]);
      // "0VU" badge on the scale rail
      ctx.fillStyle = "rgba(255,220,80,0.9)";
      ctx.font = `bold ${Math.round(7 * dpr) / dpr}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText("0VU", scaleX + SCALE_W / 2, yRef + 3);
      ctx.restore();
    }

    // Channel labels at bottom
    ctx.fillStyle = LABELBR;
    ctx.font = `bold ${Math.round(9 * dpr) / dpr}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText("L", CH_GAP + chW / 2, H - 2);
    ctx.fillText("R", CH_GAP + chW + CH_GAP + chW / 2, H - 2);
    // Peak dB readout — shows VU units in VU mode, dBFS otherwise
    const peakStr = (db: number) => {
      if (db <= VU_MIN_DB) return '—';
      if (meterMode === "vu") {
        const vu = db - vuRefDbfs;
        return vu >= 0 ? `+${vu.toFixed(1)}` : vu.toFixed(1);
      }
      return db >= 0 ? `+${db.toFixed(1)}` : db.toFixed(1);
    };
    const hotThresh = meterMode === "vu" ? (vuRefDbfs - 3) : -3;
    ctx.fillStyle = smL >= hotThresh ? "#ff6060" : LABEL;
    ctx.font = `${Math.round(7.5 * dpr) / dpr}px monospace`;
    ctx.fillText(peakStr(smL), CH_GAP + chW / 2, H - LBL_H + 4);
    ctx.fillStyle = smR >= hotThresh ? "#ff6060" : LABEL;
    ctx.fillText(peakStr(smR), CH_GAP + chW + CH_GAP + chW / 2, H - LBL_H + 4);

    ctx.restore();
  }, [analyserL, analyserR, isVisualizerActive, currentTrack, meterMode, vuRefDbfs, isExternalYT]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef);

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Analog VU needle meter ───────────────────────────────────────────────────

export function AnalogVuCanvas({ meterMode, vuRefDbfs = -18 }: { meterMode: MeterMode; vuRefDbfs?: number }) {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { analyserL, analyserR, isVisualizerActive, isExternalYT, currentTrack, isPlaying } = useMediaPlayer();
  const raf    = useRef(0);
  const stateL = useRef<ChannelMeterState>(makeMeterState());
  const stateR = useRef<ChannelMeterState>(makeMeterState());
  // Live playback flag — the draw callback's dep list omits isPlaying, so a
  // closure read goes stale after pause and keeps the needle sim running.
  const isPlayingRef4 = useRef(isPlaying);
  useEffect(() => { isPlayingRef4.current = isPlaying; }, [isPlaying]);
  const needleLin = useRef([0, 0]); // [L, R] linear amplitude for needle

  // VU scale: degrees from 12-o'clock (positive = clockwise)
  const VU_PTS: [number, number][] = [
    [-20,-57],[-10,-33],[-7,-24],[-5,-17],[-3,-10],[-2,-7],[-1,-3],[0,2],[1,7],[2,12],[3,18]
  ];

  function vuToDeg(vu: number): number {
    const c = Math.max(-20, Math.min(3, vu));
    for (let i = 0; i < VU_PTS.length - 1; i++) {
      if (c >= VU_PTS[i][0] && c <= VU_PTS[i+1][0]) {
        const t = (c - VU_PTS[i][0]) / (VU_PTS[i+1][0] - VU_PTS[i][0]);
        return VU_PTS[i][1] + t * (VU_PTS[i+1][1] - VU_PTS[i][1]);
      }
    }
    return c <= -20 ? -57 : 18;
  }

  function degToRad(deg: number): number {
    return (90 - deg) * Math.PI / 180;
  }

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    // Get dBFS reading then compute VU level
    const isYT4 = !!(currentTrack?.youtubeId || currentTrack?.mediaType === "youtube") || isExternalYT;
    // Live flag via ref — see VuPopupCanvas.
    const playingNow4 = isPlayingRef4.current || isExternalYT || domAudioPlaying();
    const now4 = Date.now();
    const rawL = micOn() ? micDb() : (analyserL && isVisualizerActive && !isYT4) ? computeMeterDb(analyserL, meterMode, stateL.current)
               : -120;
    const rawR = micOn() ? micDb() : (analyserR && isVisualizerActive && !isYT4) ? computeMeterDb(analyserR, meterMode, stateR.current)
               : -120;

    // Needle VU ballistics (300ms) on linear amplitude
    const linL = Math.pow(10, rawL / 20);
    const linR = Math.pow(10, rawR / 20);
    if (isVisualizerActive) {
      needleLin.current[0] += (linL - needleLin.current[0]) * 0.054;
      needleLin.current[1] += (linR - needleLin.current[1]) * 0.054;
    } else {
      needleLin.current[0] *= 0.94;
      needleLin.current[1] *= 0.94;
    }
    const vuL = 20 * Math.log10(Math.max(needleLin.current[0], 1e-10)) - vuRefDbfs;
    const vuR = 20 * Math.log10(Math.max(needleLin.current[1], 1e-10)) - vuRefDbfs;

    const GAP = 6;
    const mW  = (W - GAP * 3) / 2;
    const mH  = H - 4;

    const drawMeter = (x: number, needleVu: number, label: string) => {
      const cx = x + mW / 2;
      const cy = H - 4;
      const r  = Math.min(mW * 0.9, mH * 0.92);

      // Outer border
      ctx.fillStyle = "#2a2a28";
      ctx.beginPath();
      ctxRoundRect(ctx, x, 2, mW, mH - 2, 4);
      ctx.fill();

      // Face background (semi-circle clip)
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r + 2, Math.PI, 0, true);
      ctx.rect(x, 2, mW, cy - 2);
      ctx.clip();
      // Cream gradient
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      grad.addColorStop(0, "#faf0d8");
      grad.addColorStop(1, "#e8d8b0");
      ctx.fillStyle = grad;
      ctx.fillRect(x, 2, mW, H);
      ctx.restore();

      // Red zone arc (0 VU to +3 VU)
      const redStart = degToRad(vuToDeg(0));
      const redEnd   = degToRad(vuToDeg(3));
      ctx.fillStyle = "rgba(220,40,0,0.13)";
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r * 0.85, redEnd, redStart, false);
      ctx.closePath();
      ctx.fill();

      // Scale arc
      ctx.strokeStyle = "#666655";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.78, degToRad(vuToDeg(3)), degToRad(vuToDeg(-20)), true);
      ctx.stroke();

      // Scale marks + labels
      for (const [vu, deg] of VU_PTS) {
        const angle = degToRad(deg);
        const major = vu % 5 === 0 || vu === 3 || vu === -3 || vu === -7 || vu === 0;
        const isRed = vu >= 0;
        const outerR = r * (major ? 0.84 : 0.81);
        const innerR = r * (major ? 0.70 : 0.76);
        ctx.strokeStyle = isRed ? "#c03000" : "#333322";
        ctx.lineWidth = major ? 1.5 : 0.8;
        ctx.beginPath();
        ctx.moveTo(cx + innerR * Math.cos(angle), cy - innerR * Math.sin(angle));
        ctx.lineTo(cx + outerR * Math.cos(angle), cy - outerR * Math.sin(angle));
        ctx.stroke();
        // Labels at major marks
        if (major) {
          const lR = r * 0.56;
          ctx.fillStyle = isRed ? "#aa2000" : "#333322";
          ctx.font = `bold ${Math.max(5.5, r * 0.10)}px sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const lbl = vu > 0 ? `+${vu}` : String(vu);
          ctx.fillText(lbl, cx + lR * Math.cos(angle), cy - lR * Math.sin(angle));
        }
      }

      // "VU" text
      ctx.fillStyle = "#333322";
      ctx.font = `bold ${Math.max(7, r * 0.13)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("VU", cx, cy - r * 0.32);

      // Channel label
      ctx.fillStyle = "#555544";
      ctx.font = `${Math.max(6, r * 0.10)}px sans-serif`;
      ctx.fillText(label, cx, cy - r * 0.17);

      // Ref level label
      ctx.fillStyle = "#888877";
      ctx.font = `${Math.max(5, r * 0.085)}px monospace`;
      ctx.fillText(`0VU=${vuRefDbfs}dBFS`, cx, cy - r * 0.06);

      // Pivot circle
      ctx.fillStyle = "#1a1a18";
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.035, 0, Math.PI * 2);
      ctx.fill();

      // Needle
      const needleDeg   = vuToDeg(needleVu);
      const needleAngle = degToRad(needleDeg);
      const needleLen   = r * 0.82;
      ctx.strokeStyle = "#1a1a18";
      ctx.lineWidth = Math.max(1.2, r * 0.02);
      ctx.lineCap = "round";
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1.5;
      ctx.shadowOffsetY = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + needleLen * Math.cos(needleAngle), cy - needleLen * Math.sin(needleAngle));
      ctx.stroke();
      ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    };

    // Draw BG
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    drawMeter(GAP,           vuL, "L");
    drawMeter(GAP*2 + mW,    vuR, "R");

    ctx.restore();
  }, [analyserL, analyserR, isVisualizerActive, currentTrack, meterMode, vuRefDbfs, isExternalYT]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef);

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Spectrum analyser ────────────────────────────────────────────────────────

/** Vivid, high-contrast bar colour — classic hardware spectrum style */
function specBarColor(v: number): string {
  if (v > 0.90) return "#ff2020";   // red    — clipping / very hot
  if (v > 0.75) return "#ff8000";   // orange — warning
  if (v > 0.50) return "#ffdd00";   // yellow — moderate
  if (v > 0.20) return "#44ff88";   // bright green — nominal
  return "#00cc55";                  // dim green   — noise floor (still visible)
}

/** `analyser`: meter this source instead of the main player (the DJ decks and
 *  master out — Gordon 2026-10-31). */
export function SpectrumCanvas({ specSmooth = 0.85, analyser }: { specSmooth?: number; analyser?: AnalyserNode | null }) {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mp = useMediaPlayer();
  const own = analyser !== undefined;
  const analyserL = own ? analyser : mp.analyserL;
  const analyserR = own ? null : mp.analyserR;
  const isVisualizerActive = own ? !!analyser : mp.isVisualizerActive;
  const isExternalYT = own ? false : mp.isExternalYT;
  const { currentTrack, isPlaying } = mp;
  const raf          = useRef(0);
  const peakBarsRef  = useRef<number[]>([]);
  const peakTimesRef = useRef<number[]>([]);

  // Frequency grid marks (Hz) — logarithmic feel
  const FREQ_MARKS = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  // dBFS grid lines
  const DB_MARKS   = [0, -12, -24, -36, -48, -60, -72];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    // Layout margins
    const PAD_L  = 30;   // left: dBFS scale
    const PAD_B  = 16;   // bottom: freq scale
    const PAD_R  = 4;
    const PAD_T  = 4;
    const plotW  = W - PAD_L - PAD_R;
    const plotH  = H - PAD_T - PAD_B;

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    const al = analyserL || analyserR;
    const nyquist = al ? ((al as any).context?.sampleRate || 44100) / 2 : 22050;

    // ── dBFS grid lines ───────────────────────────────────────────
    ctx.lineWidth = 0.5;
    ctx.font = `${Math.round(8 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    for (const db of DB_MARKS) {
      const ratio = (-db) / 84;  // analyser range: minDecibels=-84, maxDecibels=0 → 84 dB span
      const y = PAD_T + ratio * plotH;
      ctx.strokeStyle = db === 0 ? GRIDHI : GRID;
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(W - PAD_R, y); ctx.stroke();
      ctx.fillStyle = db === 0 ? LABELBR : LABEL;
      ctx.fillText(`${db}`, PAD_L - 2, y + 3.5);
    }
    // "dBFS" rotated label
    ctx.fillStyle = "rgba(180,200,180,0.4)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.save(); ctx.translate(10, PAD_T + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center"; ctx.fillText("dBFS", 0, 0); ctx.restore();

    // ── Frequency bars + per-bar peak hold ───────────────────────
    if (al && isVisualizerActive) {
      const bins = al.frequencyBinCount;
      const freqData = new Uint8Array(bins);

      // Proper dB range for music — default maxDecibels=-30 clips mastered content
      if (al.maxDecibels !== 0)   al.maxDecibels  = 0;
      if (al.minDecibels !== -84) al.minDecibels  = -84;
      al.smoothingTimeConstant = specSmooth;

      al.getByteFrequencyData(freqData);

      const BARS = Math.min(bins, 200);
      const barW = plotW / BARS;
      const now  = Date.now();

      // TRUE log-frequency mapping that MATCHES the axis labels below (which use
      // log10(freq/20)/log10(nyquist/20)). The old pow(i,1.8) bin mapping did NOT
      // match the labels, so content read at the wrong frequency and the top
      // octaves (5–20 kHz) looked missing. Each bar now covers a log-spaced band
      // [fLo,fHi) and takes the MAX bin in it, so real HF peaks are shown.
      const binHz  = nyquist / (bins - 1);
      const logMin = Math.log10(20);
      const logMax = Math.log10(nyquist);
      const logSpan = logMax - logMin;

      // Initialise peak arrays when BARS count changes
      if (peakBarsRef.current.length !== BARS) {
        peakBarsRef.current  = new Array(BARS).fill(0);
        peakTimesRef.current = new Array(BARS).fill(0);
      }

      for (let i = 0; i < BARS; i++) {
        const fLo = Math.pow(10, logMin + logSpan * (i / BARS));
        const fHi = Math.pow(10, logMin + logSpan * ((i + 1) / BARS));
        const loBin = Math.max(0, Math.floor(fLo / binHz));
        const hiBin = Math.min(bins - 1, Math.max(loBin, Math.ceil(fHi / binHz) - 1));
        let mx = 0;
        for (let b = loBin; b <= hiBin; b++) { const fv = freqData[b]; if (fv > mx) mx = fv; }
        const v       = mx / 255;
        const barH    = v * plotH;
        const x       = PAD_L + i * barW;

        // Draw bar — always full opacity, vivid colour
        if (barH > 0.5) {
          ctx.fillStyle = specBarColor(v);
          ctx.fillRect(x, PAD_T + plotH - barH, barW - 0.5, barH);
        }

        // Update per-bar peak hold
        const peak = peakBarsRef.current[i];
        if (v > peak) {
          peakBarsRef.current[i]  = v;
          peakTimesRef.current[i] = now;
        } else if (now - peakTimesRef.current[i] > PEAK_HOLD_MS) {
          peakBarsRef.current[i] = Math.max(0, peak - 0.006);
        }

        // Draw peak tick — bright white, coloured above threshold
        if (peak > 0.02) {
          const peakY = PAD_T + plotH - peak * plotH - 1.5;
          ctx.fillStyle = peak > 0.9 ? "#ff6060" : peak > 0.5 ? "#ffee44" : "#aaffcc";
          ctx.fillRect(x, peakY, barW - 0.5, 2);
        }
      }
    }

    // ── Frequency grid lines + labels (drawn AFTER bars so labels are on top)
    ctx.lineWidth = 0.5;
    ctx.font = `${Math.round(8 * dpr) / dpr}px monospace`;
    ctx.textAlign = "center";
    for (const freq of FREQ_MARKS) {
      if (freq > nyquist) break;
      const xRatio = Math.log10(freq / 20) / Math.log10(nyquist / 20);
      const x = PAD_L + xRatio * plotW;
      ctx.strokeStyle = GRID;
      ctx.setLineDash([2, 4]);
      ctx.beginPath(); ctx.moveTo(x, PAD_T); ctx.lineTo(x, PAD_T + plotH); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = freq === 1000 || freq === 10000 ? LABELBR : LABEL;
      const lbl = freq >= 1000 ? `${freq / 1000}k` : `${freq}`;
      ctx.fillText(lbl, x, H - 2);
    }

    // Bottom axis label
    ctx.fillStyle = "rgba(180,200,180,0.4)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    ctx.fillText("Hz", W - PAD_R, H - 2);

    ctx.restore();
  }, [analyserL, analyserR, isVisualizerActive, currentTrack, specSmooth]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef);

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Oscilloscope ─────────────────────────────────────────────────────────────

export function ScopeCanvas() {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { analyserL, isVisualizerActive, currentTrack } = useMediaPlayer();
  const raf = useRef(0);

  const AMP_MARKS = [1.0, 0.5, 0, -0.5, -1.0];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    const PAD_L = 28;
    const PAD_B = 14;
    const PAD_R = 4;
    const PAD_T = 4;
    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    // Amplitude grid lines
    ctx.font = `${Math.round(8 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    for (const amp of AMP_MARKS) {
      const y = PAD_T + (0.5 - amp * 0.5) * plotH;
      ctx.strokeStyle = amp === 0 ? GRIDHI : GRID;
      ctx.lineWidth = amp === 0 ? 0.7 : 0.4;
      ctx.setLineDash(amp === 0 ? [] : [3, 5]);
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(W - PAD_R, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = amp === 0 ? LABELBR : LABEL;
      const lbl = amp > 0 ? `+${amp.toFixed(1)}` : amp.toFixed(1);
      ctx.fillText(lbl, PAD_L - 2, y + 3);
    }

    // Amplitude label
    ctx.fillStyle = "rgba(180,200,180,0.4)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.save(); ctx.translate(9, PAD_T + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center"; ctx.fillText("Amplitude", 0, 0); ctx.restore();

    // Vertical time grid (4 divisions)
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 0.4;
    ctx.setLineDash([3, 5]);
    for (let d = 1; d < 4; d++) {
      const x = PAD_L + (d / 4) * plotW;
      ctx.beginPath(); ctx.moveTo(x, PAD_T); ctx.lineTo(x, PAD_T + plotH); ctx.stroke();
    }
    ctx.setLineDash([]);

    // Waveform
    if (analyserL && isVisualizerActive) {
      const buf = new Float32Array(analyserL.fftSize);
      analyserL.getFloatTimeDomainData(buf);

      // Glow effect
      ctx.strokeStyle = "#22d4d4";
      ctx.lineWidth   = 1;
      ctx.shadowColor = "#22d4d4";
      ctx.shadowBlur  = 6 * dpr;
      ctx.globalAlpha = 0.25;
      ctx.beginPath();
      for (let i = 0; i < buf.length; i++) {
        const x = PAD_L + (i / (buf.length - 1)) * plotW;
        const y = PAD_T + (0.5 - buf[i] * 0.5) * plotH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Main trace
      ctx.globalAlpha = 1;
      ctx.shadowBlur  = 3 * dpr;
      ctx.strokeStyle = "#22d4d4";
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      for (let i = 0; i < buf.length; i++) {
        const x = PAD_L + (i / (buf.length - 1)) * plotW;
        const y = PAD_T + (0.5 - buf[i] * 0.5) * plotH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // Time axis label
    ctx.globalAlpha = 1;
    ctx.fillStyle = "rgba(180,200,180,0.4)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.textAlign = "center";
    ctx.fillText("Time →", PAD_L + plotW / 2, H - 2);

    ctx.restore();
  }, [analyserL, isVisualizerActive, currentTrack]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef);

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Spectrogram (waterfall) ──────────────────────────────────────────────────

/** `analyser`: draw this source instead of the main player (DJ decks, master out). */
export function SpectrogramCanvas({ analyser }: { analyser?: AnalyserNode | null } = {}) {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const mp = useMediaPlayer();
  const own = analyser !== undefined;
  const analyserL = own ? analyser : mp.analyserL;
  const isVisualizerActive = own ? !!analyser : mp.isVisualizerActive;
  const { currentTrack } = mp;
  const raf = useRef(0);

  const FREQ_MARKS = [100, 500, 1000, 5000, 10000, 20000];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;

    const PAD_L  = 28;
    const PAD_R  = 4;
    const plotW  = W - PAD_L - PAD_R;

    // Ensure offscreen canvas — preserve history when dimensions change (rescale)
    const newW = Math.round(plotW * dpr);
    const newH = Math.round(H * dpr);
    if (!offscreenRef.current
      || offscreenRef.current.width  !== newW
      || offscreenRef.current.height !== newH) {
      const nextOff = document.createElement("canvas");
      nextOff.width  = newW;
      nextOff.height = newH;
      // Scale existing history into the new canvas so the waterfall doesn't reset
      if (offscreenRef.current && offscreenRef.current.width > 0 && offscreenRef.current.height > 0) {
        const nextCtx = nextOff.getContext("2d")!;
        nextCtx.drawImage(offscreenRef.current, 0, 0, newW, newH);
      }
      offscreenRef.current = nextOff;
    }
    const off    = offscreenRef.current;
    const offCtx = off.getContext("2d")!;

    // Only scroll and write new column when playing — so history is preserved when paused
    if (analyserL && isVisualizerActive) {
      offCtx.drawImage(off, -dpr, 0);
      offCtx.clearRect(off.width - dpr, 0, dpr, off.height);
      const freqData = new Uint8Array(analyserL.frequencyBinCount);

      // Proper dB range for music
      if (analyserL.maxDecibels !== 0)   analyserL.maxDecibels  = 0;
      if (analyserL.minDecibels !== -84) analyserL.minDecibels  = -84;

      analyserL.getByteFrequencyData(freqData);
      for (let i = 0; i < freqData.length; i++) {
        const v   = freqData[i] / 255;
        const hue = 240 - v * 240;
        offCtx.fillStyle = `hsla(${hue},100%,${30 + v * 50}%,${v})`;
        const yFrac = 1 - i / freqData.length;
        const segH  = off.height / freqData.length;
        offCtx.fillRect(off.width - dpr, yFrac * off.height, dpr, segH + 0.5);
      }
    }

    // Main canvas
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(off, 0, 0, off.width, off.height, PAD_L * dpr, 0, plotW * dpr, canvas.height);

    // Frequency axis (Y) labels
    ctx.save();
    ctx.scale(dpr, dpr);
    const al = analyserL;
    const nyquist = al ? ((al as any).context?.sampleRate || 44100) / 2 : 22050;
    ctx.font = `${Math.round(7.5 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    for (const freq of FREQ_MARKS) {
      if (freq > nyquist) break;
      const yRatio = 1 - freq / nyquist;
      const y = yRatio * H;
      ctx.fillStyle = LABEL;
      const lbl = freq >= 1000 ? `${freq / 1000}k` : `${freq}`;
      ctx.fillText(lbl, PAD_L - 2, y + 3);
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(W - PAD_R, y); ctx.stroke();
    }
    // Y axis label
    ctx.fillStyle = "rgba(180,200,180,0.4)";
    ctx.font = `${Math.round(7 * dpr) / dpr}px monospace`;
    ctx.save(); ctx.translate(8, H / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center"; ctx.fillText("Hz", 0, 0); ctx.restore();
    // Time arrow
    ctx.textAlign = "right";
    ctx.fillText("Time →", W - PAD_R, H - 2);
    ctx.restore();
  }, [analyserL, isVisualizerActive, currentTrack]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef); // history is preserved in draw via rescale

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Goniometer ───────────────────────────────────────────────────────────────

export function GoniometerCanvas({ gonioFade = 0.16 }: { gonioFade?: number }) {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { analyserL, analyserR, isVisualizerActive, isExternalYT, currentTrack, isPlaying } = useMediaPlayer();
  const raf        = useRef(0);
  const persistRef = useRef<HTMLCanvasElement | null>(null);
  const corrRef    = useRef(0);  // latest computed correlation — read in non-persisted pass
  const rmsLRef    = useRef(-120);
  const rmsRRef    = useRef(-120);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // Self-heal sizing per frame — the sheet mounts mid-animation on mobile,
    // so mount-time sizing sees a 0×0 rect and the meter stays blank.
    if (!ensureCanvasSize(canvas, containerRef.current, dpr)) return;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;

    const CORR_H  = 26 * dpr;   // correlation bar height in physical px (taller than before)
    const PAD_B   = CORR_H / dpr + 4;
    const plotH   = H - PAD_B;

    // Ensure persistence canvas
    if (!persistRef.current || persistRef.current.width !== canvas.width || persistRef.current.height !== canvas.height) {
      const p = document.createElement("canvas");
      p.width  = canvas.width;
      p.height = canvas.height;
      persistRef.current = p;
    }
    const persist = persistRef.current;
    const pCtx    = persist.getContext("2d")!;

    // Fade persistence
    pCtx.fillStyle = `rgba(2,4,8,${gonioFade})`;
    pCtx.fillRect(0, 0, persist.width, persist.height);

    const cx     = persist.width / 2;
    const cy     = (plotH / H) * persist.height / 2;
    const radius = Math.min(cx, cy) * 0.85;

    if (analyserL && analyserR && isVisualizerActive) {
      const bufL = new Float32Array(analyserL.fftSize);
      const bufR = new Float32Array(analyserR.fftSize);
      analyserL.getFloatTimeDomainData(bufL);
      analyserR.getFloatTimeDomainData(bufR);

      // Lissajous trace — drawn on persistence canvas so it fades naturally
      pCtx.strokeStyle = "#00e5ff";
      pCtx.lineWidth   = 1.5 * dpr;
      pCtx.globalAlpha = 0.92;
      pCtx.shadowColor = "#00e5ff";
      pCtx.shadowBlur  = 10 * dpr;
      pCtx.beginPath();

      const step = Math.max(1, Math.floor(bufL.length / 512));
      let first = true;
      for (let i = 0; i < bufL.length; i += step) {
        const l = bufL[i], r = bufR[i];
        const px = cx + (l - r) * radius * 0.7071;
        const py = cy - (l + r) * radius * 0.7071;
        first ? pCtx.moveTo(px, py) : pCtx.lineTo(px, py);
        first = false;
      }
      pCtx.stroke();
      pCtx.globalAlpha = 1;
      pCtx.shadowBlur  = 0;

      // Compute correlation + RMS — stored in refs; drawn in non-persisted pass so they never fade
      let sumLL = 0, sumRR = 0, sumLR = 0;
      for (let i = 0; i < bufL.length; i++) {
        sumLL += bufL[i] * bufL[i];
        sumRR += bufR[i] * bufR[i];
        sumLR += bufL[i] * bufR[i];
      }
      corrRef.current = sumLR / (Math.sqrt(sumLL * sumRR) + 1e-10);
      rmsLRef.current = 20 * Math.log10(Math.max(Math.sqrt(sumLL / bufL.length), 1e-10));
      rmsRRef.current = 20 * Math.log10(Math.max(Math.sqrt(sumRR / bufR.length), 1e-10));
    }

    // ── Composite to display canvas ───────────────────────────────
    // Fill background (must use physical px here — we're before scale)
    ctx.fillStyle = "#020408";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Draw persistence canvas at full physical size (not CSS px — that would squish to top-left on hi-DPI)
    ctx.drawImage(persist, 0, 0, persist.width, persist.height, 0, 0, canvas.width, canvas.height);

    // ── Non-persisted decorations ─────────────────────────────────
    ctx.save();
    ctx.scale(dpr, dpr);
    const cxL = W / 2;
    const cyL = plotH / 2;
    const rad = Math.min(cxL, cyL) * 0.85;

    // ── Outer glow ring (sci-fi border) ───────────────────────────
    ctx.shadowColor = "rgba(0,229,255,0.25)";
    ctx.shadowBlur = 6;
    ctx.strokeStyle = "rgba(0,229,255,0.22)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cxL, cyL, rad * 1.04, 0, Math.PI * 2); ctx.stroke();
    ctx.shadowBlur = 0;

    // ── Concentric graticule rings at 25 / 50 / 75 / 100 % ───────
    for (const [frac, alpha] of [[0.25, 0.05], [0.5, 0.07], [0.75, 0.06], [1.0, 0.0]] as [number, number][]) {
      ctx.strokeStyle = `rgba(0,229,255,${alpha})`;
      ctx.lineWidth = 0.4;
      ctx.beginPath(); ctx.arc(cxL, cyL, rad * frac, 0, Math.PI * 2); ctx.stroke();
    }

    // ── Radial tick marks every 45° (8 ticks) ─────────────────────
    ctx.strokeStyle = "rgba(0,229,255,0.12)";
    ctx.lineWidth = 0.4;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(cxL + Math.cos(angle) * rad * 0.92, cyL + Math.sin(angle) * rad * 0.92);
      ctx.lineTo(cxL + Math.cos(angle) * rad * 1.04, cyL + Math.sin(angle) * rad * 1.04);
      ctx.stroke();
    }

    // ── Outer diamond (M/S/L/R frame) — single clean shape ────────
    ctx.strokeStyle = "rgba(0,229,255,0.28)";
    ctx.lineWidth = 0.8;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(cxL,         cyL - rad);   // M top
    ctx.lineTo(cxL + rad,   cyL);         // R right
    ctx.lineTo(cxL,         cyL + rad);   // S bottom
    ctx.lineTo(cxL - rad,   cyL);         // L left
    ctx.closePath();
    ctx.stroke();

    // ── Corner dots at M / R / S / L ──────────────────────────────
    for (const [px, py] of [[cxL, cyL - rad], [cxL + rad, cyL], [cxL, cyL + rad], [cxL - rad, cyL]] as [number, number][]) {
      ctx.fillStyle = "rgba(0,229,255,0.6)";
      ctx.beginPath(); ctx.arc(px, py, 2, 0, Math.PI * 2); ctx.fill();
    }

    // ── Corner labels: M / S / L / R ──────────────────────────────
    ctx.font = `bold ${Math.round(8.5 * dpr) / dpr}px "JetBrains Mono", monospace`;
    ctx.fillStyle = "rgba(0,229,255,0.75)";
    ctx.textAlign = "center";
    ctx.fillText("M",   cxL,           cyL - rad - 9);
    ctx.fillText("S",   cxL,           cyL + rad + 13);
    ctx.textAlign = "right";
    ctx.fillText("L",   cxL - rad - 5, cyL + 4);
    ctx.textAlign = "left";
    ctx.fillText("R",   cxL + rad + 5, cyL + 4);

    // ── Retro amber diagonal reference lines (L/R channel axes at 45°) ──
    const sq45 = rad * 0.9;
    ctx.strokeStyle = "rgba(200,160,60,0.25)";
    ctx.lineWidth = 0.7;
    ctx.setLineDash([4, 5]);
    // NE-SW diagonal
    ctx.beginPath();
    ctx.moveTo(cxL - sq45 * 0.7071, cyL - sq45 * 0.7071);
    ctx.lineTo(cxL + sq45 * 0.7071, cyL + sq45 * 0.7071);
    ctx.stroke();
    // NW-SE diagonal
    ctx.beginPath();
    ctx.moveTo(cxL + sq45 * 0.7071, cyL - sq45 * 0.7071);
    ctx.lineTo(cxL - sq45 * 0.7071, cyL + sq45 * 0.7071);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── Corner live measurements ───────────────────────────────────
    const cFont = `${Math.round(7.5 * dpr) / dpr}px "JetBrains Mono", monospace`;
    ctx.font = cFont;
    // Top-left: L RMS
    const rmsLdb = rmsLRef.current;
    ctx.fillStyle = rmsLdb > -6 ? "#ff4444" : rmsLdb > -14 ? "#ffa020" : "rgba(0,229,255,0.55)";
    ctx.textAlign = "left";
    ctx.fillText(`L ${rmsLdb > -120 ? (rmsLdb >= 0 ? "+0" : rmsLdb.toFixed(1)) : "—"}`, 4, 14);
    // Top-right: R RMS
    const rmsRdb = rmsRRef.current;
    ctx.fillStyle = rmsRdb > -6 ? "#ff4444" : rmsRdb > -14 ? "#ffa020" : "rgba(0,229,255,0.55)";
    ctx.textAlign = "right";
    ctx.fillText(`R ${rmsRdb > -120 ? (rmsRdb >= 0 ? "+0" : rmsRdb.toFixed(1)) : "—"}`, W - 4, 14);
    // Bottom-left: stereo width (inverted correlation = width percentage)
    const widthPct = Math.round(Math.abs(corrRef.current) * 100);
    ctx.fillStyle = "rgba(0,229,255,0.45)";
    ctx.textAlign = "left";
    ctx.fillText(`W ${(100 - widthPct)}%`, 4, plotH - 6);
    // Bottom-right: live correlation value
    const corrSign = corrRef.current >= 0 ? "+" : "";
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(0,229,255,0.45)";
    ctx.fillText(`${corrSign}${corrRef.current.toFixed(2)}`, W - 4, plotH - 6);

    // ── Correlation bar — non-persisted so it's always crisp ─────
    const corr   = corrRef.current;
    const barTop = H - PAD_B + 2;
    const barH   = PAD_B - 5;
    const barX   = 6;
    const barW   = W - 12;
    const midX   = barX + barW / 2;

    // Background track with inner glow
    ctx.fillStyle = "#070e12";
    ctx.shadowColor = "rgba(0,229,255,0.1)";
    ctx.shadowBlur = 3;
    ctx.beginPath();
    ctxRoundRect(ctx, barX, barTop, barW, barH, 4);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Gradient fill — green zone positive, red zone negative
    const fillPx = (corr / 2) * barW;
    if (Math.abs(fillPx) > 0.5) {
      const grad = ctx.createLinearGradient(
        fillPx >= 0 ? midX : midX + fillPx, 0,
        fillPx >= 0 ? midX + fillPx : midX, 0
      );
      if (corr >= 0) {
        grad.addColorStop(0, "rgba(0,200,80,0.4)");
        grad.addColorStop(1, corr > 0.8 ? "#00ff90" : corr > 0.5 ? "#00e060" : "#00cc50");
      } else {
        grad.addColorStop(0, "rgba(255,40,40,0.4)");
        grad.addColorStop(1, "#ff3030");
      }
      ctx.fillStyle = grad;
      ctx.shadowColor = corr >= 0 ? "#00ff60" : "#ff3030";
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctxRoundRect(
        ctx,
        fillPx >= 0 ? midX : midX + fillPx,
        barTop + 1.5,
        Math.abs(fillPx),
        barH - 3,
        3
      );
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // Tick marks at -1, -0.5, 0, +0.5, +1
    for (const frac of [-1, -0.5, 0, 0.5, 1]) {
      const tx = midX + (frac / 2) * barW;
      ctx.fillStyle = frac === 0 ? "rgba(255,255,255,0.9)" : "rgba(0,229,255,0.35)";
      ctx.fillRect(tx - (frac === 0 ? 0.75 : 0.5), barTop, frac === 0 ? 1.5 : 1, barH);
    }

    // Labels: "-1" left, value+title centre, "+1" right
    const labelY = barTop + barH / 2 + 4;
    ctx.font = `bold ${Math.round(8 * dpr) / dpr}px "JetBrains Mono", monospace`;
    ctx.fillStyle = "rgba(0,229,255,0.55)";
    ctx.textAlign = "left";
    ctx.fillText("-1", barX + 3, labelY);
    ctx.textAlign = "right";
    ctx.fillText("+1", barX + barW - 3, labelY);

    // Centre: label + live numeric value
    const sign = corr >= 0 ? "+" : "";
    const centreText = isVisualizerActive
      ? `CORR  ${sign}${corr.toFixed(2)}`
      : "PHASE CORRELATION";
    ctx.textAlign = "center";
    ctx.fillStyle = corr >= 0.7 ? "#80ffb0" : corr >= 0 ? "#40e080" : "#ff8888";
    ctx.fillText(centreText, midX, labelY);

    ctx.restore();
  }, [analyserL, analyserR, isVisualizerActive, currentTrack, gonioFade]);

  useEffect(() => {
    let lastDraw = 0;
    const loop = () => { const now = Date.now(); if (!document.hidden && !scrollingNow() && now - lastDraw >= _drawIntervalMs) { lastDraw = now; draw(); } raf.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf.current);
  }, [draw]);
  useResizeCanvas(canvasRef, containerRef, () => { persistRef.current = null; });

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

// ─── Drag-resize handle ───────────────────────────────────────────────────────

function PanelResizer({
  direction,
  onDrag,
  className: extraCls,
}: {
  direction: "h" | "v";
  onDrag: (delta: number) => void;
  className?: string;
}) {
  const lastPos = useRef(0);
  const dragging = useRef(false);

  return (
    <div
      className={cn(
        "flex-shrink-0 group relative flex items-center justify-center z-10 select-none",
        direction === "h"
          ? "w-2 cursor-col-resize hover:bg-primary/25 active:bg-primary/40"
          : "h-2 cursor-row-resize hover:bg-primary/25 active:bg-primary/40",
        extraCls
      )}
      onPointerDown={e => {
        e.preventDefault();
        dragging.current = true;
        lastPos.current = direction === "h" ? e.clientX : e.clientY;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={e => {
        if (!dragging.current || e.buttons === 0) return;
        const cur = direction === "h" ? e.clientX : e.clientY;
        const delta = cur - lastPos.current;
        lastPos.current = cur;
        onDrag(delta);
      }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
    >
      <div
        className={cn(
          "bg-border/70 rounded-full opacity-40 group-hover:opacity-100 transition-opacity",
          direction === "h" ? "w-px h-10" : "h-px w-10"
        )}
      />
    </div>
  );
}

// ─── Meter panel wrapper ──────────────────────────────────────────────────────

function MeterPanel({
  type,
  style,
  className: extraCls,
  children,
  onDoubleClick,
  settingsContent,
  isFullscreen,
  headerExtra,
  // Drag-to-swap props
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDrop,
  isDragOver,
}: {
  type: VisType;
  style?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
  onDoubleClick?: () => void;
  settingsContent?: React.ReactNode;
  isFullscreen?: boolean;
  headerExtra?: React.ReactNode;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDragOver?: boolean;
}) {
  const [settingsOpen, setSettingsOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 640; // sm breakpoint — open on desktop, closed on mobile
  });

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border min-h-0 min-w-0 transition-all",
        isDragOver ? "border-primary/70 bg-primary/5 ring-1 ring-primary/40" : "border-border",
        isFullscreen && "ring-2 ring-primary",
        extraCls
      )}
      style={style}
      onDoubleClick={onDoubleClick}
      onDragOver={e => { e.preventDefault(); onDragEnter?.(e); }}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* ── Panel header ── */}
      <div className="flex items-center justify-between gap-1.5 px-1.5 py-1 flex-shrink-0 bg-muted/50 border-b border-border">
        {/* Drag handle + title — draggable area */}
        <div
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            onDragStart?.();
          }}
          onDragEnd={onDragEnd}
          className="flex items-center gap-1 cursor-grab active:cursor-grabbing flex-1 min-w-0 select-none"
          title="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3 opacity-30 group-hover:opacity-60 flex-shrink-0 text-foreground" />
          <span
            className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ background: VIS_COLORS[type], boxShadow: `0 0 4px ${VIS_COLORS[type]}` }}
          />
          <span className="text-xs font-bold tracking-widest uppercase text-foreground truncate">
            {VIS_FULL[type]}
          </span>
        </div>
        {/* Inline header extras (Trail buttons for goniometer, etc.) */}
        {headerExtra && <div className="flex items-center gap-1 flex-shrink-0 mx-1">{headerExtra}</div>}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {settingsContent && (
            <button
              onClick={(e) => { e.stopPropagation(); setSettingsOpen(!settingsOpen); }}
              className="p-1 rounded hover:bg-muted/80 text-foreground/60 hover:text-foreground transition-colors"
              title="Settings"
            >
              <span className="text-sm">⚙</span>
            </button>
          )}
          {onDoubleClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
              className="p-1 rounded hover:bg-muted/80 text-foreground/60 hover:text-foreground transition-colors"
              title="Toggle fullscreen"
            >
              {isFullscreen ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      {settingsOpen && settingsContent && (
        <div className="bg-muted/30 border-b border-border/40 px-2.5 py-2 flex-shrink-0 flex flex-col gap-1.5 overflow-x-auto overflow-y-auto max-h-[130px]">
          {settingsContent}
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden" style={{ background: BG }}>
        {children}
      </div>
    </div>
  );
}

// ─── InlineAudioMeters — tabbed meter panel for the media player page ────────

// ─── Per-meter education + calibration reference ──────────────────────────────

/** Reference (dB offset) levels the meters can be calibrated to — the 0 VU line. */
const REF_LEVELS: { db: number; note: string }[] = [
  { db: -14, note: "Streaming — Spotify, Apple Music, YouTube target" },
  { db: -18, note: "Mixing / EBU R128-ish — the classic gain-staging reference" },
  { db: -20, note: "SMPTE — US film & TV" },
  { db: -23, note: "EBU R128 broadcast (−23 LUFS)" },
];

/** What each meter measures + how it's calibrated (shown in the "i" popover). */
const METER_INFO: Record<VisType, { title: string; body: string; scale: string }> = {
  vu: {
    title: "VU Meters (LED)",
    body: "Averages loudness over ~300 ms — how LOUD it feels, not instant peaks. 0 VU is your reference level; green is safe, yellow/red is hot. Set 0 VU to your target and gain-stage to it.",
    scale: "0 VU = your chosen dBFS reference (see the 0 VU ref selector).",
  },
  "needle-vu": {
    title: "Analog VU Needle",
    body: "The classic moving-coil VU — slow ballistics that track perceived loudness. Sits at 0 VU on well-levelled material.",
    scale: "0 VU = your chosen dBFS reference (shown on the meter face).",
  },
  spectrum: {
    title: "Spectrum Analyser",
    body: "Splits the sound into frequency bands (20 Hz–20 kHz, log scale) and shows the energy in each. Any single band reads far below the peak/VU meters because full-scale energy is spread across ~1000 bins — that's normal, not a fault.",
    scale: "Absolute dBFS · 0 = full scale (top). Per-band, so it maxes well below 0.",
  },
  scope: {
    title: "Oscilloscope",
    body: "The raw waveform — amplitude over time. Shows transients, stereo shape, and clipping (flat tops hitting the rails).",
    scale: "Centre line = silence · top/bottom rails = 0 dBFS full scale.",
  },
  spectrogram: {
    title: "Spectrogram",
    body: "Frequency (vertical) over time (scrolling), brightness = energy. Reveals sweeps, risers, harmonics and noise as the track evolves.",
    scale: "Brightness = energy per frequency over time.",
  },
  goniometer: {
    title: "Goniometer / Vectorscope",
    body: "Stereo field + phase. A vertical line = mono, a 45° tilt = one side louder, a round blob = wide stereo. The correlation bar warns of phase / mono-compatibility problems.",
    scale: "Vertical = mono · wide = stereo · correlation bar flags phase issues.",
  },
};

/** A little "i" button that opens an education popover for the given meter,
 *  including how it's calibrated. Manages its own open state. */
function MeterInfoButton({ vis, vuRefLevel }: { vis: VisType; vuRefLevel?: number }) {
  const [open, setOpen] = useState(false);
  const info = METER_INFO[vis];
  if (!info) return null;
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={`About ${info.title}`}
        aria-label={`About ${info.title}`}
        className="h-5 w-5 inline-flex items-center justify-center rounded-full border border-border/50 text-[10px] font-bold italic text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
      >i</button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-6 z-50 w-64 max-w-[80vw] p-3 rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl text-[11px] leading-relaxed">
            <p className="font-semibold mb-1">{info.title}</p>
            <p className="text-muted-foreground">{info.body}</p>
            <p className="mt-2 pt-2 border-t border-border/50 font-mono text-[10px] text-foreground/80">{info.scale}</p>
            {(vis === "vu" || vis === "needle-vu") && vuRefLevel != null && (
              <p className="mt-1 font-mono text-[10px] text-amber-500">Calibrated now: 0 VU = {vuRefLevel} dBFS</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * InlineAudioMeters
 * Tabbed meter panel (VU, Needle, Spectrum, Scope, Spectrogram, Goniometer).
 * Defaults to Spectrum Analyser. Reuses the same canvas components as the popup.
 */
export function InlineAudioMeters({
  className,
  height,
  toolbarActions,
}: {
  className?: string;
  /** Fixed pixel height. Omit (or undefined) for flex-fill mode — fills parent flex container. */
  height?: number;
  toolbarActions?: React.ReactNode;
}) {
  const [activeVis, setActiveVis]   = useState<VisType>("spectrum");
  const [meterMode, setMeterMode]   = useState<MeterMode>("vu");
  const [vuRefLevel, setVuRefLevel] = useState(-14);
  const [specSmooth, setSpecSmooth] = useState(0.85);
  const [gonioFade,  setGonioFade]  = useState(0.16);

  const VIS_ORDER_INLINE: VisType[] = ["spectrum", "scope", "spectrogram", "vu", "needle-vu", "goniometer"];

  function renderCanvas() {
    switch (activeVis) {
      case "vu":          return <VuPopupCanvas meterMode={meterMode} vuRefDbfs={vuRefLevel} />;
      case "needle-vu":   return <AnalogVuCanvas meterMode={meterMode} vuRefDbfs={vuRefLevel} />;
      case "spectrum":    return <SpectrumCanvas specSmooth={specSmooth} />;
      case "scope":       return <ScopeCanvas />;
      case "spectrogram": return <SpectrogramCanvas />;
      case "goniometer":  return <GoniometerCanvas gonioFade={gonioFade} />;
      default:            return null;
    }
  }

  const isFixed = typeof height === 'number';

  return (
    <div className={cn("flex flex-col gap-0", !isFixed && "flex-1 min-h-0", className)}>
      {/* Tab bar */}
      <div className="flex items-center gap-1 pb-1.5 flex-wrap flex-shrink-0">
        {VIS_ORDER_INLINE.map(vis => (
          <button
            key={vis}
            onClick={() => setActiveVis(vis)}
            className={cn(
              "px-2.5 py-0.5 rounded text-[11px] font-mono font-bold border transition-all",
              activeVis === vis
                ? "text-black border-transparent"
                : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30 bg-transparent"
            )}
            style={activeVis === vis ? { background: VIS_COLORS[vis], borderColor: VIS_COLORS[vis] } : undefined}
          >
            {VIS_LABELS[vis]}
          </button>
        ))}

        {toolbarActions && <div className="ml-auto flex-shrink-0">{toolbarActions}</div>}

        {/* Per-meter education "i" button — always visible for the active meter. */}
        <div className="ml-1"><MeterInfoButton vis={activeVis} vuRefLevel={vuRefLevel} /></div>

        {/* Settings for active meter */}
        {(activeVis === "vu" || activeVis === "needle-vu") && (
          <div className="ml-2 flex items-center gap-1 flex-wrap">
            {(Object.keys(METER_MODE_LABELS) as MeterMode[]).slice(0, 4).map(mode => (
              <button key={mode} onClick={() => setMeterMode(mode)}
                title={METER_MODE_DESC[mode]}
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-mono border transition-all",
                  meterMode === mode ? "bg-primary text-primary-foreground border-primary" : "border-border/40 text-muted-foreground hover:text-foreground"
                )}
              >{METER_MODE_LABELS[mode]}</button>
            ))}
            {/* Calibration (0 VU reference) — clearly labelled so it's obvious what
                level the meters are calibrated to. */}
            <span className="text-[10px] text-muted-foreground ml-1">0 VU=</span>
            {REF_LEVELS.map(({ db, note }) => (
              <button key={db} onClick={() => setVuRefLevel(db)} title={note}
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-mono border transition-all",
                  vuRefLevel === db ? "bg-amber-500 text-black border-amber-400" : "border-border/40 text-muted-foreground hover:text-foreground"
                )}
              >{db}</button>
            ))}
            <span className="text-[9px] text-amber-500/80 font-mono ml-0.5">= 0 VU · {vuRefLevel} dBFS</span>
          </div>
        )}
        {(activeVis === "spectrum" || activeVis === "scope" || activeVis === "spectrogram" || activeVis === "goniometer") && (
          <span className="ml-2 text-[9px] text-muted-foreground font-mono">{METER_INFO[activeVis].scale}</span>
        )}
        {activeVis === "spectrum" && (
          <div className="ml-2 flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">Smooth:</span>
            <input type="range" min="0" max="0.95" step="0.05" value={specSmooth}
              onChange={e => setSpecSmooth(parseFloat(e.target.value))}
              className="w-20 h-1.5 accent-primary"
            />
            <span className="text-[10px] text-muted-foreground">{(specSmooth * 100).toFixed(0)}%</span>
          </div>
        )}
        {activeVis === "goniometer" && (
          <div className="ml-2 flex items-center gap-1">
            {[{ l: "Slow", v: 0.08 }, { l: "Med", v: 0.16 }, { l: "Fast", v: 0.32 }].map(({ l, v }) => (
              <button key={l} onClick={() => setGonioFade(v)}
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-mono border transition-all",
                  gonioFade === v ? "bg-primary text-primary-foreground border-primary" : "border-border/40 text-muted-foreground hover:text-foreground"
                )}
              >{l}</button>
            ))}
          </div>
        )}
      </div>

      {/* Canvas area — fixed height when height prop given; flex-1 fill when not */}
      <div
        className={cn(
          "relative w-full rounded-lg overflow-hidden border border-border/40 flex flex-col",
          !isFixed && "flex-1 min-h-0"
        )}
        style={{ ...(isFixed ? { height } : {}), background: BG }}
      >
        {renderCanvas()}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function GlobalVuStrip({ playerActive }: { playerActive: boolean }) {
  const stripCanvasRef          = useRef<HTMLCanvasElement>(null);
  const stripContainerRef       = useRef<HTMLDivElement>(null);
  const mobileStripCanvasRef    = useRef<HTMLCanvasElement>(null);
  const mobileStripContainerRef = useRef<HTMLDivElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  useCloseMenuWhenOpen(popupOpen);
  const pathname = usePathname();
  const { settings: brandingSettings } = useBranding();

  // Close popup whenever the user navigates (sidebar link, etc.)
  useEffect(() => {
    setPopupOpen(false);
  }, [pathname]);

  // Any menu item closes the meter window — also one for the page you're on,
  // a hub tab, or a menu entry that opens a panel instead of a new page
  // (Gordon 2026-11-04: "when I click any other menu item with meter window
  // open the meter window should close").
  useEffect(() => {
    if (!popupOpen) return;
    const onClick = (e: MouseEvent) => {
      const el = e.target as Element | null;
      if (!el || el.closest('.vu-popup-inner')) return;
      if (el.closest('[data-sidebar="sidebar"], [data-sidebar="menu-button"], [data-sidebar="menu-item"], [role="dialog"][data-state="open"] nav')) setPopupOpen(false);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [popupOpen]);

  // Close popup when chat button, app logo, or media player thumbnail is clicked
  useEffect(() => {
    const handler = () => setPopupOpen(false);
    window.addEventListener('close-vu-popup', handler);
    return () => window.removeEventListener('close-vu-popup', handler);
  }, []);

  // Open from anywhere via window.dispatchEvent(new Event('open-vu-popup')).
  // Per Gordon: "Audio Editor & Mastering play button must trigger main vu
  // and meter pop up page" — the audio editor fires this when its Play
  // Selection button is hit so the full meter rig appears as soon as
  // sound is going through the routed audio element.
  useEffect(() => {
    const handler = () => setPopupOpen(true);
    window.addEventListener('open-vu-popup', handler);
    return () => window.removeEventListener('open-vu-popup', handler);
  }, []);

  // Default active meters from super-admin settings (all 6 by default)
  const defaultMeters = (brandingSettings.vuDefaultMeters ?? ["vu", "needle-vu", "spectrum", "scope", "spectrogram", "goniometer"]) as VisType[];
  const [activeMeters, setActiveMeters] = useState<Set<VisType>>(new Set<VisType>(defaultMeters));
  // Sync when admin changes the default
  useEffect(() => {
    setActiveMeters(new Set<VisType>(defaultMeters));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingSettings.vuDefaultMeters?.join(",")]);

  // VU strip style prefs (persisted to localStorage)
  const [vuStripPrefs, setVuStripPrefs] = useState<VuStripPrefs>(() => {
    try {
      const stored = localStorage.getItem("vu-strip-prefs");
      if (stored) return { ...DEFAULT_VU_STRIP_PREFS, ...JSON.parse(stored) };
    } catch {}
    return DEFAULT_VU_STRIP_PREFS;
  });
  const updateVuStripPrefs = (patch: Partial<VuStripPrefs>) => {
    const next = { ...vuStripPrefs, ...patch };
    setVuStripPrefs(next);
    try { localStorage.setItem("vu-strip-prefs", JSON.stringify(next)); } catch {}
  };

  const [stripStyleOpen, setStripStyleOpen] = useState(false);

  // Meter modes and settings
  const [meterMode, setMeterMode]           = useState<MeterMode>("vu");
  const [fullscreenPanel, setFullscreenPanel] = useState<VisType | null>(null);
  const [vuRefLevel, setVuRefLevel]         = useState(-14);
  const [specSmooth, setSpecSmooth]         = useState(0.85);
  const [gonioFade, setGonioFade]           = useState(0.16);

  // ── Layout: user-defined meter order & per-meter flex sizes ──────────────
  const [meterOrder, setMeterOrder] = useState<VisType[]>(() => {
    if (typeof window === "undefined") return [...DEFAULT_METER_ORDER];
    const stored = lsGet<VisType[]>("vu-meter-order", [...DEFAULT_METER_ORDER]);
    // Validate — ensure all meter types are present (add missing ones at end)
    const valid = stored.filter(v => (DEFAULT_METER_ORDER as string[]).includes(v));
    const missing = DEFAULT_METER_ORDER.filter(v => !valid.includes(v));
    return [...valid, ...missing];
  });
  const [meterFlexes, setMeterFlexes] = useState<Record<VisType, number>>(() => {
    if (typeof window === "undefined") return { ...DEFAULT_METER_FLEXES };
    return { ...DEFAULT_METER_FLEXES, ...lsGet<Partial<Record<VisType, number>>>("vu-meter-flexes", {}) };
  });
  const [gramFlex, setGramFlex] = useState<number>(() => {
    if (typeof window === "undefined") return 1.75;
    return lsGet<number>("vu-gram-flex", 1.75);
  });
  const [topFlex, setTopFlex] = useState<number>(() => {
    if (typeof window === "undefined") return 1;
    return lsGet<number>("vu-top-flex", 1);
  });
  /** Which meter sits in the full-width bottom row. Anything can go there now —
   *  Spectrogram is the historical default. Drag-swap with any top meter moves
   *  it to/from the bottom. Persisted across sessions. */
  const [bottomRowVis, setBottomRowVis] = useState<VisType>(() => {
    if (typeof window === "undefined") return "spectrum";
    return lsGet<VisType>("vu-bottom-row", "spectrum");
  });

  // ── Drag-to-swap ──────────────────────────────────────────────────────────
  const [draggedVis, setDraggedVis] = useState<VisType | null>(null);
  const [dragOverVis, setDragOverVis] = useState<VisType | null>(null);

  const swapMeters = (a: VisType | null, b: VisType | null) => {
    if (!a || !b || a === b) return;
    // If the bottom-row slot is involved, swap which meter occupies it. The
    // bottom slot is rendered independently of meterOrder, so this is enough
    // to let ANY meter (including spectrogram) be the bottom full-width one.
    if (a === bottomRowVis || b === bottomRowVis) {
      const newBottom: VisType = a === bottomRowVis ? b : a;
      setBottomRowVis(newBottom);
      lsSet("vu-bottom-row", newBottom);
    }
    setMeterOrder(prev => {
      const next = [...prev];
      const ai = next.indexOf(a), bi = next.indexOf(b);
      if (ai === -1 || bi === -1) return prev;
      [next[ai], next[bi]] = [next[bi], next[ai]];
      lsSet("vu-meter-order", next);
      return next;
    });
  };

  const updateMeterFlex = (vis: VisType, flex: number) => {
    setMeterFlexes(prev => {
      const next = { ...prev, [vis]: Math.max(0.1, flex) };
      lsSet("vu-meter-flexes", next);
      return next;
    });
  };

  // ── Layout presets ────────────────────────────────────────────────────────
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdmin();
  const [presets, setPresets] = useState<MeterLayoutPreset[]>(() => {
    if (typeof window === "undefined") return [];
    return lsGet<MeterLayoutPreset[]>("vu-meter-presets", []);
  });
  const [presetName, setPresetName] = useState("");
  const [showPresets, setShowPresets] = useState(false);

  const savePreset = (name: string) => {
    const preset: MeterLayoutPreset = {
      name,
      order: [...meterOrder],
      flexes: { ...meterFlexes },
      activeMeters: [...activeMeters],
    };
    setPresets(prev => {
      const filtered = prev.filter(p => p.name !== name);
      const next = [...filtered, preset].slice(-6); // max 6 presets
      lsSet("vu-meter-presets", next);
      // Sync to Firestore for logged-in users
      if (user) {
        setDoc(doc(db, "users", user.uid, "preferences", "vuLayout"), { presets: next }, { merge: true }).catch(() => {});
      }
      return next;
    });
    setPresetName("");
  };

  const loadPreset = (preset: MeterLayoutPreset) => {
    setMeterOrder([...preset.order]);
    setMeterFlexes({ ...DEFAULT_METER_FLEXES, ...preset.flexes });
    setActiveMeters(new Set(preset.activeMeters));
    lsSet("vu-meter-order", preset.order);
    lsSet("vu-meter-flexes", preset.flexes);
  };

  const deletePreset = (name: string) => {
    setPresets(prev => {
      const next = prev.filter(p => p.name !== name);
      lsSet("vu-meter-presets", next);
      if (user) {
        setDoc(doc(db, "users", user.uid, "preferences", "vuLayout"), { presets: next }, { merge: true }).catch(() => {});
      }
      return next;
    });
  };

  // Has the saved layout been hydrated from Firestore yet? We must not auto-save
  // (below) until AFTER the load, or we'd overwrite the account layout with this
  // browser's defaults on first paint.
  const layoutHydrated = useRef(false);
  const layoutSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Whether this person already has a layout of their own (account or this
  // browser). null = not determined yet. Users WITHOUT one get the admin-saved
  // app default for their device class (see effect below).
  const [hasPersonalLayout, setHasPersonalLayout] = useState<boolean | null>(null);

  // Load presets AND the user's live working layout from Firestore on mount, so
  // each user's arrangement is remembered across devices/sessions — not just in
  // this browser's localStorage (Gordon: "remember the layout of each user").
  useEffect(() => {
    if (!user) {
      setHasPersonalLayout(lsGet<VisType[] | null>("vu-meter-order", null) !== null);
      layoutHydrated.current = true;
      return;
    }
    getDoc(doc(db, "users", user.uid, "preferences", "vuLayout"))
      .then(snap => {
        let personal = lsGet<VisType[] | null>("vu-meter-order", null) !== null;
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data.presets) && data.presets.length > 0) {
            setPresets(data.presets);
            lsSet("vu-meter-presets", data.presets);
          }
          const c = data.current;
          if (c && typeof c === "object") {
            personal = true;
            if (Array.isArray(c.order)) {
              const valid = (c.order as VisType[]).filter(v => (DEFAULT_METER_ORDER as string[]).includes(v));
              const missing = DEFAULT_METER_ORDER.filter(v => !valid.includes(v));
              const order = [...valid, ...missing];
              setMeterOrder(order); lsSet("vu-meter-order", order);
            }
            if (c.flexes && typeof c.flexes === "object") {
              const f = { ...DEFAULT_METER_FLEXES, ...c.flexes }; setMeterFlexes(f); lsSet("vu-meter-flexes", f);
            }
            if (typeof c.bottomRow === "string") { setBottomRowVis(c.bottomRow); lsSet("vu-bottom-row", c.bottomRow); }
            if (typeof c.gramFlex === "number") { setGramFlex(c.gramFlex); lsSet("vu-gram-flex", c.gramFlex); }
            if (typeof c.topFlex === "number") { setTopFlex(c.topFlex); lsSet("vu-top-flex", c.topFlex); }
            if (c.stripPrefs && typeof c.stripPrefs === "object") {
              const sp = { ...DEFAULT_VU_STRIP_PREFS, ...c.stripPrefs }; setVuStripPrefs(sp);
              try { localStorage.setItem("vu-strip-prefs", JSON.stringify(sp)); } catch {}
            }
          }
        }
        setHasPersonalLayout(personal);
      })
      .catch(() => { setHasPersonalLayout(lsGet<VisType[] | null>("vu-meter-order", null) !== null); })
      .finally(() => { layoutHydrated.current = true; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // ── Admin app-default layout ──────────────────────────────────────────────
  // Users with no layout of their own start from the admin-saved default for
  // their device class (Layouts → "App default" in this popup, admin-only).
  // Their own tweaks immediately take over from there, as usual.
  const adminLayoutApplied = useRef(false);
  useEffect(() => {
    if (adminLayoutApplied.current || hasPersonalLayout !== false) return;
    const layouts = (brandingSettings as any)?.vuDefaultLayouts;
    if (!layouts || typeof layouts !== "object") return;
    const c = layouts[vuDeviceClass()] || layouts.desktop;
    if (!c || typeof c !== "object") return;
    adminLayoutApplied.current = true;
    if (Array.isArray(c.order)) {
      const valid = (c.order as VisType[]).filter(v => (DEFAULT_METER_ORDER as string[]).includes(v));
      const missing = DEFAULT_METER_ORDER.filter(v => !valid.includes(v));
      setMeterOrder([...valid, ...missing]);
    }
    if (c.flexes && typeof c.flexes === "object") setMeterFlexes({ ...DEFAULT_METER_FLEXES, ...c.flexes });
    if (typeof c.bottomRow === "string") setBottomRowVis(c.bottomRow as VisType);
    if (typeof c.gramFlex === "number") setGramFlex(c.gramFlex);
    if (typeof c.topFlex === "number") setTopFlex(c.topFlex);
    if (c.stripPrefs && typeof c.stripPrefs === "object") setVuStripPrefs({ ...DEFAULT_VU_STRIP_PREFS, ...c.stripPrefs });
    if (Array.isArray(c.activeMeters) && c.activeMeters.length > 0) setActiveMeters(new Set(c.activeMeters as VisType[]));
    if (typeof c.meterMode === "string") setMeterMode(c.meterMode as MeterMode);
    if (typeof c.vuRefLevel === "number") setVuRefLevel(c.vuRefLevel);
    if (typeof c.specSmooth === "number") setSpecSmooth(c.specSmooth);
    if (typeof c.gonioFade === "number") setGonioFade(c.gonioFade);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPersonalLayout, brandingSettings]);

  // Admin: save the CURRENT arrangement as the app-wide default for a device
  // class. Everything is captured — order, sizes, active panels, signal modes.
  const [savingDefaultFor, setSavingDefaultFor] = useState<VuDeviceClass | null>(null);
  const saveAsAppDefault = async (device: VuDeviceClass) => {
    if (!user) return;
    setSavingDefaultFor(device);
    try {
      const snapshot = {
        order: [...meterOrder],
        flexes: { ...meterFlexes },
        bottomRow: bottomRowVis,
        gramFlex,
        topFlex,
        stripPrefs: vuStripPrefs,
        activeMeters: [...activeMeters],
        meterMode,
        vuRefLevel,
        specSmooth,
        gonioFade,
      };
      const existing = ((brandingSettings as any)?.vuDefaultLayouts) || {};
      await saveAppearanceSettings({ vuDefaultLayouts: { ...existing, [device]: snapshot } } as any, user.uid);
    } catch (e) {
      console.error("save vu default layout failed", e);
    } finally {
      setSavingDefaultFor(null);
    }
  };

  // Auto-persist the LIVE working layout to the user's account (debounced) so a
  // change on any device is remembered everywhere. Named presets ("save
  // different layouts") remain separate — this is the always-on "current".
  useEffect(() => {
    if (!user || !layoutHydrated.current) return;
    if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = setTimeout(() => {
      const current = {
        order: meterOrder,
        flexes: meterFlexes,
        bottomRow: bottomRowVis,
        gramFlex,
        topFlex,
        stripPrefs: vuStripPrefs,
      };
      setDoc(doc(db, "users", user.uid, "preferences", "vuLayout"), { current }, { merge: true }).catch(() => {});
    }, 800);
    return () => { if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, meterOrder, meterFlexes, bottomRowVis, gramFlex, topFlex, vuStripPrefs]);

  const { isPlaying, isVisualizerActive, currentTrack } = useMediaPlayer();
  // coπ's microphone lights the main VU too, with or without music playing
  // (Gordon 2026-10-31: "mic in signal should activate main VU meter").
  const [micLive, setMicLive] = useState(false);
  useEffect(() => {
    const sync = () => setMicLive(micOn());
    sync();
    window.addEventListener("bb-mic", sync);
    return () => window.removeEventListener("bb-mic", sync);
  }, []);
  const stripShown = isPlaying || micLive;
  // Tell the layout whether the strip's column is in use, so the open player
  // window can take that space when the meter is hidden (Gordon 2026-11-01:
  // "if the vu meter is not playing then stretch the media player window all
  // the way to the left … now there's a hole").
  useEffect(() => {
    document.documentElement.dataset.vuStrip = stripShown ? "on" : "off";
  }, [stripShown]);
  useEffect(() => () => { delete document.documentElement.dataset.vuStrip; }, []);

  useVuStripCanvas(stripCanvasRef, stripContainerRef as any, vuStripPrefs);
  useVuHorizontalStripCanvas(mobileStripCanvasRef, mobileStripContainerRef as any);

  const toggleMeter = (vis: VisType) => {
    setActiveMeters(prev => {
      const next = new Set(prev);
      if (next.has(vis)) {
        if (next.size <= 1) return prev;
        next.delete(vis);
      } else {
        next.add(vis);
      }
      return next;
    });
  };

  const stripBottom = playerActive ? "var(--player-height)" : "0px";
  const VIS_ORDER: VisType[] = ["vu", "needle-vu", "spectrum", "scope", "spectrogram", "goniometer"];

  const gonioHeaderExtra = (
    <div className="flex items-center gap-1">
      {([{ label: "S", value: 0.08 }, { label: "M", value: 0.16 }, { label: "F", value: 0.32 }]).map(({ label, value }) => (
        <button key={label} onClick={() => setGonioFade(value)}
          className={cn("px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border transition-all",
            gonioFade === value
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30"
          )}>
          {label}
        </button>
      ))}
      <span className="text-[9px] text-muted-foreground ml-0.5">trail</span>
    </div>
  );

  const hasVU    = activeMeters.has("vu");
  const hasNeedle = activeMeters.has("needle-vu");
  const hasSp    = activeMeters.has("spectrum");
  const hasSc    = activeMeters.has("scope");
  const hasGram  = activeMeters.has("spectrogram");
  const hasGonio = activeMeters.has("goniometer");
  const hasCenter = hasNeedle || hasSc;  // Analogue VU + Scope now share the centre column

  function renderPanelCanvas(vis: VisType): React.ReactNode {
    switch(vis) {
      case "vu":          return <VuPopupCanvas meterMode={meterMode} vuRefDbfs={vuRefLevel} />;
      case "needle-vu":   return <AnalogVuCanvas meterMode={meterMode} vuRefDbfs={vuRefLevel} />;
      case "spectrum":    return <SpectrumCanvas specSmooth={specSmooth} />;
      case "scope":       return <ScopeCanvas />;
      case "spectrogram": return <SpectrogramCanvas />;
      case "goniometer":  return <GoniometerCanvas gonioFade={gonioFade} />;
      default:            return null;
    }
  }

  /** Shared size slider row rendered at the bottom of every meter's settings panel */
  function renderSizeSlider(vis: VisType): React.ReactNode {
    return (
      <div className="flex items-center gap-2 border-t border-border/30 pt-1.5 mt-0.5">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider shrink-0 w-16">Width:</span>
        <input
          type="range"
          min="0.15"
          max="3.5"
          step="0.05"
          value={meterFlexes[vis] ?? DEFAULT_METER_FLEXES[vis]}
          onChange={e => updateMeterFlex(vis, parseFloat(e.target.value))}
          className="w-28 h-2 accent-primary"
        />
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {((meterFlexes[vis] ?? DEFAULT_METER_FLEXES[vis]) / 1).toFixed(2)}×
        </span>
        <button
          onClick={() => updateMeterFlex(vis, DEFAULT_METER_FLEXES[vis])}
          className="text-[9px] text-muted-foreground/60 hover:text-foreground transition-colors px-1"
          title="Reset to default size"
        >↺</button>
      </div>
    );
  }

  function renderSettingsContent(vis: VisType): React.ReactNode {
    if (vis === "vu" || vis === "needle-vu") {
      return (
        <>
          {/* Row 1: Signal measurement type */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider shrink-0 w-16">Signal:</span>
            {(Object.keys(METER_MODE_LABELS) as MeterMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setMeterMode(mode)}
                title={METER_MODE_DESC[mode]}
                className={cn(
                  "px-2 py-0.5 rounded text-xs font-mono font-bold border transition-all",
                  meterMode === mode
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}
              >{METER_MODE_LABELS[mode]}</button>
            ))}
          </div>
          {/* Row 2: 0 VU reference level */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider shrink-0 w-16">0 VU ref:</span>
            {[-14, -18, -20, -23].map(level => (
              <button
                key={level}
                onClick={() => setVuRefLevel(level)}
                title={level === -14 ? "Streaming (Spotify / Apple Music)" : level === -18 ? "EBU R128 broadcast" : level === -20 ? "SMPTE / film & TV" : "EBU R128 strict"}
                className={cn(
                  "px-2 py-0.5 rounded text-xs font-mono font-bold border transition-all",
                  vuRefLevel === level
                    ? "bg-amber-500 text-black border-amber-400"
                    : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30"
                )}
              >{level}</button>
            ))}
            <span className="text-[9px] text-muted-foreground">dBFS — where the colour zones shift to orange/red</span>
          </div>
          {renderSizeSlider(vis)}
        </>
      );
    }
    if (vis === "spectrum") {
      return (
        <>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider shrink-0">Smoothing:</span>
            <input
              type="range"
              min="0"
              max="0.95"
              step="0.05"
              value={specSmooth}
              onChange={(e) => setSpecSmooth(parseFloat(e.target.value))}
              className="w-24 h-2 accent-primary"
            />
            <span className="text-[10px] text-muted-foreground shrink-0">{(specSmooth * 100).toFixed(0)}%</span>
            <span className="text-[9px] text-muted-foreground ml-1">— higher = slower frequency decay</span>
          </div>
          {renderSizeSlider(vis)}
        </>
      );
    }
    if (vis === "goniometer") {
      return renderSizeSlider(vis);
    }
    // scope, spectrogram
    return renderSizeSlider(vis);
  }

  // Every meter's settings row gets a header with the meter name + an "i"
  // education popover (and, for level meters, the current calibration).
  function settingsFor(vis: VisType): React.ReactNode {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <MeterInfoButton vis={vis} vuRefLevel={vuRefLevel} />
          <span className="text-[10px] font-semibold text-foreground/80">{VIS_FULL[vis]}</span>
          <span className="text-[9px] text-muted-foreground font-mono truncate">{METER_INFO[vis]?.scale}</span>
        </div>
        {renderSettingsContent(vis)}
      </div>
    );
  }

  return (
    <>
      {/* ── Desktop: fixed vertical VU strip (right edge) ─────────── */}
      {/* z-[60] keeps the strip above the chat window (z-[55]) on all pages.
          The canvas uses clearRect so non-LED pixels are transparent — the chat
          content (or page) shows through, giving the "floating bars" effect.
          ⚠️ ALWAYS MOUNTED, hidden via CSS when idle: the sizing hook's
          ResizeObserver effect runs once at mount with `if (!canvas) return` —
          when these were conditionally rendered ({isPlaying && …}) the canvas
          didn't exist at mount, the effect bailed forever, and the canvas kept
          the browser's default 300×150 backing (wrong size, wrong DPR) — a
          root cause of the "VU meter dead/first-LED-only on mobile" bug. */}
      <div
        ref={stripContainerRef}
        onClick={() => setPopupOpen(v => !v)}
        title="Audio Visualisers"
        className={cn("fixed right-0 z-[60] cursor-pointer hidden sm:block menuright:right-auto menuright:left-0", !stripShown && "!hidden")}
        style={{ top: "var(--header-height)", bottom: stripBottom, width: "var(--sidebar-width-icon)" }}
      >
        <canvas ref={stripCanvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </div>

      {/* ── Mobile: horizontal LED strip flush against the player bar ── */}
      {/* Transparent background so the chat (or page content) shows through.
          Always mounted (see note above); hidden via CSS when not playing. */}
      <div
        ref={mobileStripContainerRef}
        onClick={() => setPopupOpen(v => !v)}
        title="Audio Visualisers"
        // bottom-[56px] matches the mobile player bar's hardcoded h-[56px] exactly.
        // Using a Tailwind class (not a CSS variable in inline style) avoids Android browser
        // rounding/rendering differences that can leave a pixel gap.
        className={cn(
          "fixed left-0 w-full z-[60] cursor-pointer sm:hidden",
          "bottom-[56px]",  // player bar is always 56px tall on mobile
          !stripShown && "!hidden"
        )}
        style={{ height: "30px" }}
      >
        <canvas ref={mobileStripCanvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
      </div>

      {/* ── Popup overlay ───────────────────────────────────────────── */}
      {popupOpen && (
        <div
          className="vu-popup-overlay fixed top-0 left-0 right-0 bottom-0 z-[90] bg-black/60 sm:backdrop-blur-none flex flex-col p-0"
          style={{
            "--player-bottom": playerActive ? "var(--player-height)" : "0px",
            // Mobile: push bottom up so the player bar (always 56px) stays visible.
            // The <style> @media block handles desktop via CSS custom property.
          } as React.CSSProperties}
          onClick={() => setPopupOpen(false)}
        >
          <div
            className="vu-popup-inner bg-background w-full h-full flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* ── Popup header ──────────────────────────────────────── */}
            <div className="flex flex-col px-4 py-2.5 border-b border-border flex-shrink-0 bg-background gap-2">
              <div className="flex items-center justify-between">
                <div className="flex gap-1.5 flex-wrap">
                  {VIS_ORDER.map(vis => {
                    const isActive = activeMeters.has(vis);
                    return (
                      <button
                        key={vis}
                        onClick={() => toggleMeter(vis)}
                        title={isActive ? `Hide ${VIS_LABELS[vis]}` : `Show ${VIS_LABELS[vis]}`}
                        className={cn(
                          "px-3 py-1 rounded-md text-sm font-bold tracking-wide transition-all border",
                          isActive
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                        )}
                      >
                        {VIS_LABELS[vis]}
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2 ml-2">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border",
                    isVisualizerActive
                      ? "bg-green-500/15 text-green-500 border-green-500/40 dark:text-green-400"
                      : "bg-muted text-muted-foreground border-border"
                  )}>
                    <span className={cn(
                      "h-2 w-2 rounded-full",
                      isVisualizerActive ? "bg-green-500 animate-pulse" : "bg-muted-foreground"
                    )} />
                    {isVisualizerActive ? "Live" : "Idle"}
                  </span>
                  <button
                    onClick={() => setPopupOpen(false)}
                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                    aria-label="Close visualisers"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* ── Layout Presets ─────────────────────────────────────────── */}
              <div>
                <button
                  className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 hover:text-foreground transition-colors"
                  onClick={() => setShowPresets(v => !v)}
                >
                  <LayoutGrid className="h-3 w-3" />
                  Layouts {showPresets ? "▴" : "▾"}
                </button>
                {showPresets && (
                  <div className="mt-2 space-y-2">
                    {/* Saved preset chips */}
                    <div className="flex flex-wrap gap-1.5">
                      {presets.length === 0 && (
                        <span className="text-[10px] text-muted-foreground italic">No saved layouts yet</span>
                      )}
                      {presets.map(p => (
                        <div key={p.name} className="flex items-center gap-0">
                          <button
                            onClick={() => loadPreset(p)}
                            className="px-2 py-0.5 rounded-l text-xs font-mono border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all"
                            title={`Load layout "${p.name}"`}
                          >{p.name}</button>
                          <button
                            onClick={() => deletePreset(p.name)}
                            className="px-1.5 py-0.5 rounded-r text-xs border border-l-0 border-border/60 text-muted-foreground/50 hover:text-red-400 hover:bg-red-400/10 transition-all"
                            title={`Delete "${p.name}"`}
                          ><Trash2 className="h-2.5 w-2.5" /></button>
                        </div>
                      ))}
                    </div>
                    {/* Save new preset */}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={presetName}
                        onChange={e => setPresetName(e.target.value)}
                        placeholder="Name this layout…"
                        maxLength={20}
                        className="text-xs bg-background border border-border/60 rounded px-2 py-0.5 w-36 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary"
                        onKeyDown={e => { if (e.key === "Enter" && presetName.trim()) savePreset(presetName.trim()); }}
                      />
                      <button
                        onClick={() => { if (presetName.trim()) savePreset(presetName.trim()); }}
                        disabled={!presetName.trim()}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60 disabled:opacity-40 transition-all"
                        title="Save current layout"
                      >
                        <Plus className="h-3 w-3" /> Save
                      </button>
                      <button
                        onClick={() => {
                          setMeterOrder([...DEFAULT_METER_ORDER]);
                          setMeterFlexes({ ...DEFAULT_METER_FLEXES });
                          lsSet("vu-meter-order", DEFAULT_METER_ORDER);
                          lsSet("vu-meter-flexes", DEFAULT_METER_FLEXES);
                        }}
                        className="px-2 py-0.5 rounded text-xs border border-border/60 text-muted-foreground/60 hover:text-foreground hover:bg-muted/40 transition-all"
                        title="Reset layout to defaults"
                      >↺ Reset</button>
                    </div>
                    {/* Admin: save the current arrangement as the app-wide
                        default per device class. Users who already made their
                        own layout keep theirs; everyone else starts from this. */}
                    {(isAdmin || isSuperAdmin) && (
                      <div className="pt-1.5 mt-0.5 border-t border-border/30 space-y-1">
                        <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                          App default — applies to everyone without their own layout
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {(["desktop", "tablet", "mobile"] as const).map(devi => {
                            const hasSaved = !!((brandingSettings as any)?.vuDefaultLayouts?.[devi]);
                            return (
                              <button
                                key={devi}
                                onClick={() => saveAsAppDefault(devi)}
                                disabled={savingDefaultFor !== null}
                                className="px-2 py-0.5 rounded text-xs border border-primary/50 text-primary hover:bg-primary/10 disabled:opacity-40 transition-all capitalize"
                                title={`Save the current layout as the app-wide default for ${devi}${hasSaved ? " (replaces the saved one)" : ""}`}
                              >
                                {savingDefaultFor === devi ? "Saving…" : `Save for ${devi}`}{hasSaved ? " ●" : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <p className="text-[9px] text-muted-foreground/60">
                      Drag meter headers to reorder · Use ⚙ width slider to resize each panel
                      {user ? " · Layouts sync to your account" : ""}
                    </p>
                  </div>
                )}
              </div>

              {/* Strip Style — collapsible row */}
              <div>
                <button
                  className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 hover:text-foreground transition-colors"
                  onClick={() => setStripStyleOpen(v => !v)}
                >
                  Strip Style {stripStyleOpen ? "▴" : "▾"}
                </button>
                {stripStyleOpen && (
                  <div className="mt-2 space-y-2">
                    {/* Color scheme */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0">Colour:</span>
                      {(Object.keys(VU_COLOR_SCHEMES) as VuColorScheme[]).map(scheme => (
                        <button key={scheme}
                          onClick={() => updateVuStripPrefs({ colorScheme: scheme })}
                          className={cn("px-2 py-0.5 rounded text-xs font-mono font-bold border transition-all capitalize",
                            vuStripPrefs.colorScheme === scheme
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30"
                          )}>
                          {scheme.replace("mono-", "")}
                        </button>
                      ))}
                    </div>
                    {/* Glow */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0">Glow:</span>
                      {(["none", "soft", "medium", "heavy", "star"] as const).map(g => (
                        <button key={g}
                          onClick={() => updateVuStripPrefs({ glowLevel: g })}
                          className={cn("px-2 py-0.5 rounded text-xs font-mono font-bold border transition-all capitalize",
                            vuStripPrefs.glowLevel === g
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border/50 text-muted-foreground hover:text-foreground hover:border-foreground/30"
                          )}>
                          {g}
                        </button>
                      ))}
                    </div>
                    {/* Opacity */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-14 shrink-0">Opacity:</span>
                      <input type="range" min="0.2" max="1.0" step="0.05" value={vuStripPrefs.opacity}
                        onChange={e => updateVuStripPrefs({ opacity: parseFloat(e.target.value) })}
                        className="w-24 h-2" />
                      <span className="text-[10px] text-muted-foreground">{Math.round(vuStripPrefs.opacity * 100)}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Meter layout ──────────────────────────────────────── */}
            {/* Mobile: scrollable stacked column */}
            {/* Desktop: flex columns + rows with drag-resize handles */}
            <style>{`
              /* ── Desktop popup: fills exactly the working canvas area ── */
              @media (min-width: 640px) {
                .vu-popup-overlay {
                  top: var(--header-height);
                  left: var(--sidebar-width-icon);
                  right: var(--sidebar-width-icon);
                  bottom: var(--player-bottom, 0px);
                  background: transparent;
                }
                .vu-popup-inner {
                  height: 100%;
                  border-radius: 0 !important;
                  box-shadow: none;
                }
              }

              /* ── Mobile popup: constrained between header and player bar ── */
              /* Player bar is always 56px tall on mobile (hardcoded). Header height
                 is --header-height (50px). Popup must never cover either. */
              @media (max-width: 639px) {
                .vu-popup-overlay {
                  top: var(--header-height, 50px);
                  bottom: 56px; /* matches hardcoded mobile player bar h-[56px] */
                }
              }

              /* ── Mobile popup layout ─────────────────────────────── */
              @media (max-width: 639px) {
                /* Body: scrollable vertical column */
                .vu-meter-body {
                  display: flex !important;
                  flex-direction: column !important;
                  overflow-y: auto !important;
                  overflow-x: hidden;
                }
                /* Top-row stacks vertically */
                .vu-top-row {
                  display: flex !important;
                  flex-direction: column !important;
                  width: 100% !important;
                  flex: 1 0 auto !important;
                }
                /* Every direct panel in top-row: full width, fixed 220px height */
                .vu-top-row > .vu-panel {
                  width: 100% !important;
                  height: 220px !important;
                  min-height: 220px !important;
                  max-height: 220px !important;
                  flex: 0 0 220px !important;
                }
                /* Centre column: transparent wrapper — don't constrain height */
                .vu-top-row > .vu-center-col {
                  width: 100% !important;
                  height: auto !important;
                  min-height: 0 !important;
                  flex: 0 0 auto !important;
                  display: flex !important;
                  flex-direction: column !important;
                }
                /* Each panel INSIDE the centre column: same 220px as side panels */
                .vu-top-row .vu-center-col > .vu-panel {
                  width: 100% !important;
                  height: 220px !important;
                  min-height: 220px !important;
                  max-height: 220px !important;
                  flex: 0 0 220px !important;
                }
                /* Hide all resizers (both horizontal and vertical) on mobile */
                .vu-h-resizer, .vu-v-resizer { display: none !important; }
                /* Spectrogram full-width row */
                .vu-gram-panel {
                  width: 100% !important;
                  min-height: 220px !important;
                  flex: 1 0 220px !important;
                }
                /* Hide all drag handles — useless on mobile (h+v both removed above) */
                /* Single meter in body fills entire popup height */
                .vu-meter-body > :only-child { flex: 1 !important; min-height: 0 !important; }
                .vu-top-row > :only-child    { flex: 1 !important; min-height: 0 !important; }
              }
            `}</style>

            <div className="vu-meter-body flex-1 min-h-0 flex flex-col p-2 gap-0 overflow-hidden">

              {/* Fullscreen layout */}
              {fullscreenPanel && (
                <MeterPanel
                  type={fullscreenPanel}
                  style={{ flex: 1, margin: 3 }}
                  onDoubleClick={() => setFullscreenPanel(null)}
                  isFullscreen={true}
                  settingsContent={settingsFor(fullscreenPanel)}
                  headerExtra={fullscreenPanel === "goniometer" ? gonioHeaderExtra : undefined}
                >
                  {renderPanelCanvas(fullscreenPanel)}
                </MeterPanel>
              )}

              {/* Normal multi-panel layout — ORDER-DRIVEN flat row.
                  bottomRowVis decides which meter takes the full-width bottom
                  slot. Any meter can occupy that slot via drag-swap. */}
              {!fullscreenPanel && (() => {
                const topRowMeters = meterOrder.filter(v => v !== bottomRowVis && activeMeters.has(v));
                const hasBottomRow = activeMeters.has(bottomRowVis);
                const hasTop = topRowMeters.length > 0;

                return (
                  <>
                    {/* ── Top row: all non-spectrogram meters, user-ordered ── */}
                    {hasTop && (
                      <div
                        className="flex min-h-0 vu-top-row"
                        style={{ flex: topFlex, gap: 0 }}
                      >
                        {topRowMeters.map((vis, i) => (
                          <React.Fragment key={vis}>
                            <MeterPanel
                              type={vis}
                              className="vu-panel"
                              style={{
                                flex: meterFlexes[vis] ?? DEFAULT_METER_FLEXES[vis],
                                minWidth: vis === "vu" ? 48 : 80,
                                margin: 3,
                              }}
                              onDoubleClick={() => setFullscreenPanel(vis)}
                              isFullscreen={fullscreenPanel === vis}
                              settingsContent={settingsFor(vis)}
                              headerExtra={vis === "goniometer" ? gonioHeaderExtra : undefined}
                              // Drag-to-swap
                              onDragStart={() => setDraggedVis(vis)}
                              onDragEnd={() => { setDraggedVis(null); setDragOverVis(null); }}
                              onDragEnter={() => setDragOverVis(vis)}
                              onDragLeave={() => setDragOverVis(prev => prev === vis ? null : prev)}
                              onDrop={e => {
                                e.preventDefault();
                                swapMeters(draggedVis, vis);
                                setDraggedVis(null);
                                setDragOverVis(null);
                              }}
                              isDragOver={dragOverVis === vis && draggedVis !== vis}
                            >
                              {renderPanelCanvas(vis)}
                            </MeterPanel>
                            {/* Horizontal resize handle between adjacent meters */}
                            {i < topRowMeters.length - 1 && (
                              <PanelResizer
                                direction="h"
                                className="vu-h-resizer"
                                onDrag={d => {
                                  const nextVis = topRowMeters[i + 1];
                                  const ref = window.innerWidth || 1200;
                                  const fA = meterFlexes[vis] ?? DEFAULT_METER_FLEXES[vis];
                                  const fB = meterFlexes[nextVis] ?? DEFAULT_METER_FLEXES[nextVis];
                                  const delta = (d / ref) * (fA + fB);
                                  updateMeterFlex(vis, Math.max(0.1, fA + delta));
                                  updateMeterFlex(nextVis, Math.max(0.1, fB - delta));
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* Vertical resize handle between top row and spectrogram */}
                    {hasBottomRow && hasTop && (
                      <PanelResizer direction="v" onDrag={d => {
                        const total = topFlex + gramFlex;
                        const containerH = 600;
                        const delta = (d / Math.max(containerH, 200)) * total;
                        const nextTop = Math.max(0.3, topFlex + delta);
                        const nextGram = Math.max(0.15, gramFlex - delta);
                        setTopFlex(nextTop);
                        setGramFlex(nextGram);
                        lsSet("vu-top-flex", nextTop);
                        lsSet("vu-gram-flex", nextGram);
                      }} />
                    )}

                    {/* Bottom row — full-width, holds whichever meter is bottomRowVis */}
                    {hasBottomRow && (
                      <MeterPanel
                        type={bottomRowVis}
                        className="vu-gram-panel"
                        style={{ flex: gramFlex, margin: 3 }}
                        onDoubleClick={() => setFullscreenPanel(bottomRowVis)}
                        isFullscreen={fullscreenPanel === bottomRowVis}
                        settingsContent={settingsFor(bottomRowVis)}
                        headerExtra={bottomRowVis === "goniometer" ? gonioHeaderExtra : undefined}
                        onDragStart={() => setDraggedVis(bottomRowVis)}
                        onDragEnd={() => { setDraggedVis(null); setDragOverVis(null); }}
                        onDragEnter={() => setDragOverVis(bottomRowVis)}
                        onDragLeave={() => setDragOverVis(prev => prev === bottomRowVis ? null : prev)}
                        onDrop={e => {
                          e.preventDefault();
                          swapMeters(draggedVis, bottomRowVis);
                          setDraggedVis(null);
                          setDragOverVis(null);
                        }}
                        isDragOver={dragOverVis === bottomRowVis && draggedVis !== bottomRowVis}
                      >
                        {renderPanelCanvas(bottomRowVis)}
                      </MeterPanel>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
